import { useEffect, useRef, useState, useMemo } from 'react';
import { API_GET, DBRequest, useConfig } from '../../api/api';
import { Box, Button, Typography } from '@mui/material';
import TemplatePreview from '../templates/TemplatePreview';
import { useTranslation } from 'react-i18next';
import TemplateVarSwitcher from '../templates/TemplateVarSwitcher';
import { numberToString, stringToNumber } from '../special/numberConverter';
import moment from 'moment';
import 'moment/locale/de';
import { useDebounce } from 'use-debounce';
import { round } from '../special/round';
import { getInvoiceDeduction } from '../special/orderEffectiveAmount';
import { ExoAttachments } from './ExoAttachments';
import { din276_18 } from './Din276/din276-data/din276-18';
import { din276_08 } from './Din276/din276-data/din276-08';

// Simple in-memory cache for HOAI data to avoid redundant fetches
const hoaiDataCache = new Map();

const ExoDocumentPreview = ({
  dataCollection,
  previewDocument,
  documentId,
  width = 500,
  debug = false,
  attachments = [],
  showAttachments = false,
  immediate = false, // Skip debounce for preview dialogs
}) => {
  const [template, setTemplate] = useState(null);
  const [templateChange, setTemplateChange] = useState(0);
  const [templateSkonto, setTemplateSkonto] = useState('');
  const [templatePositions, setTemplatePositions] = useState('');
  const [templateOrderTable, setTemplateOrder] = useState('');
  const [templateInvoices, setTemplateInvoices] = useState('');
  // Use immediate data or debounced data based on immediate prop
  const [debouncedData] = useDebounce(dataCollection, immediate ? 0 : 700);
  const debouncedDataCollection = immediate ? dataCollection : debouncedData;
  const { t } = useTranslation();
  const config = useConfig();

  // InlineAttachments States
  const [inlineTimesheetTemplate, setInlineTimesheetTemplate] = useState(null);
  const [inlineHoaiTemplate, setInlineHoaiTemplate] = useState(null);
  const [timeTrackingEntries, setTimeTrackingEntries] = useState([]);

  useEffect(() => {
    if (!config) return;
    setTemplate(null);
    DBRequest({
      config,
      path: documentId
        ? `templates/${documentId}`
        : `templates?templateType[eq]=${previewDocument}`,
      method: API_GET,
      onResponse: (data) => {
        let template;
        if (documentId) {
          template = data.data;
        } else {
          // Filter hidden templates and prefer default template if available
          const visibleTemplates = data.data.filter((t) => !t.isHidden);
          template =
            visibleTemplates.find((t) => t.isDefault) || visibleTemplates[0];
        }
        handleResponse(template);
      },
    });
  }, [config, previewDocument, documentId]);

  //Hoai Data
  const [hoaiData, setHoaiData] = useState(null);
  const prevHoaiIdRef = useRef(null);

  useEffect(() => {
    if (!config) return;

    const positions = dataCollection?.positions;
    if (!positions || !positions.length) {
      prevHoaiIdRef.current = null;
      setHoaiData(null);
      return;
    }

    // collect unique hoaiIds and pick first one
    // if there is an order the hoaiId can be in the orderPositions instead of positions, especially if its a final invoice with no positions but only orderPositions, so we need to check both
    const hoaiEffPositions =
      dataCollection?.orderPositions || dataCollection?.positions || [];
    const uniqueHoaiIds = Array.from(
      new Set(
        hoaiEffPositions
          .map((p) => p.hoaiId)
          .filter((id) => id !== undefined && id !== null),
      ),
    );

    if (!uniqueHoaiIds.length) {
      prevHoaiIdRef.current = null;
      setHoaiData(null);
      return;
    }

    const firstHoaiId = uniqueHoaiIds[0];

    // avoid refetch if same id already loaded
    if (prevHoaiIdRef.current === firstHoaiId) return;

    prevHoaiIdRef.current = firstHoaiId;

    // Check cache first
    if (hoaiDataCache.has(firstHoaiId)) {
      setHoaiData(hoaiDataCache.get(firstHoaiId));
      return;
    }

    DBRequest({
      config,
      path: `hoai/${firstHoaiId}`,
      method: API_GET,
      onResponse: (res) => {
        // Store in cache for other instances
        hoaiDataCache.set(firstHoaiId, res.data);
        setHoaiData(res.data);
      },
    });
  }, [config, dataCollection?.positions]);

  // Load inline templates when inlineAttachments change
  useEffect(() => {
    if (!config) return;

    const inlineAttachments = dataCollection?.inlineAttachments || [];

    // Timesheet Template laden
    if (inlineAttachments.includes('timesheet')) {
      DBRequest({
        config,
        path: 'templates?templateType[eq]=inline_timesheet',
        method: API_GET,
        onResponse: (data) => {
          const tmpl = data.data?.filter((t) => !t.isHidden)[0];
          if (tmpl) setInlineTimesheetTemplate(tmpl);
        },
      });
    } else {
      // Clear template when timesheet is deselected
      setInlineTimesheetTemplate(null);
    }

    // HOAI Template laden
    if (inlineAttachments.includes('hoaiCalculation')) {
      DBRequest({
        config,
        path: 'templates?templateType[eq]=hoai_calculation',
        method: API_GET,
        onResponse: (data) => {
          const tmpl = data.data?.filter((t) => !t.isHidden)[0];
          if (tmpl) setInlineHoaiTemplate(tmpl);
        },
      });
    } else {
      // Clear template when HOAI is deselected
      setInlineHoaiTemplate(null);
    }
  }, [config, dataCollection?.inlineAttachments]);

  // Load time tracking entries (for timesheet and other uses)
  // Note: Always load when IDs are present - inlineAttachments check is only for content generation
  useEffect(() => {
    if (!config || !dataCollection?.timeTrackingData?.ids?.length) {
      setTimeTrackingEntries([]);
      return;
    }

    const { ids } = dataCollection.timeTrackingData;
    const idsQuery = ids.map((id) => `ids[]=${id}`).join('&');

    DBRequest({
      config,
      // Include relationships for complete data (user, project, workPackage)
      path: `timetrackings?${idsQuery}&with[]=user&with[]=project&with[]=workPackage`,
      method: API_GET,
      onResponse: (res) => setTimeTrackingEntries(res.data || []),
    });
  }, [config, dataCollection?.timeTrackingData?.ids]);

  // Generate inline attachment content
  const inlineAttachmentContent = useMemo(() => {
    if (!dataCollection?.inlineAttachments?.length) return '';

    let inlineContent = '';

    // Timesheet
    if (
      dataCollection.inlineAttachments.includes('timesheet') &&
      inlineTimesheetTemplate &&
      timeTrackingEntries?.length
    ) {
      const adjustments = dataCollection.timeTrackingData?.adjustments || {};
      const billingConfig =
        dataCollection.timeTrackingData?.billingConfig || {};

      inlineContent += generateTimesheetContent(
        inlineTimesheetTemplate,
        timeTrackingEntries,
        billingConfig,
        adjustments,
      );
    }

    // HOAI
    if (
      dataCollection.inlineAttachments.includes('hoaiCalculation') &&
      inlineHoaiTemplate &&
      dataCollection.positions?.length
    ) {
      inlineContent += generateHoaiContent(
        inlineHoaiTemplate,
        dataCollection.positions,
        hoaiData ? [hoaiData] : [],
      );
    }

    return inlineContent;
  }, [
    dataCollection?.inlineAttachments,
    dataCollection?.timeTrackingData,
    dataCollection?.positions,
    inlineTimesheetTemplate,
    inlineHoaiTemplate,
    timeTrackingEntries,
    hoaiData,
  ]);

  // Combine template with inline attachment content
  const templateWithInlineAttachments = useMemo(() => {
    if (!template) return null;
    if (!inlineAttachmentContent) return template;

    return {
      ...template,
      content: template.content + inlineAttachmentContent,
    };
  }, [template, inlineAttachmentContent]);

  function handleResponse(data) {
    var newTemplate = data;
    var templateVars = newTemplate.vars;
    var templateMeta = newTemplate.metaData || {};

    // INVOICES (PROGRESS BILLING) -------------------------------
    const invoicesStartKey = '%%hideIfNoInvoicesStart%%';
    const invoicesEndKey = '%%hideIfNoInvoicesEnd%%';
    const invoicesReplaceKey = '%%invoicesSection%%';
    const invoicesTemplateArea = getTemplateArea(
      newTemplate.content,
      invoicesStartKey,
      invoicesEndKey,
      invoicesReplaceKey,
    );
    newTemplate.content = invoicesTemplateArea.modifiedContent;
    // Add the custom positions var to the var array of the template
    const customInvoicesVar = {
      value: invoicesReplaceKey,
      name: 'Document Invoices',
      preview: invoicesTemplateArea.contentBetweenKeys[0],
    };
    templateVars.push(customInvoicesVar);
    const invoicesTemplateString = invoicesTemplateArea.contentBetweenKeys[0];
    setTemplateInvoices(invoicesTemplateString);

    // ORDER (PROGRESS BILLING) -------------------------------
    // Your start and end keys
    const orderStartKey = '%%orderTableStart%%';
    const orderEndKey = '%%orderTableEnd%%';
    const orderReplaceKey = '%%orderTable%%';
    const orderTemplateArea = getTemplateArea(
      newTemplate.content,
      orderStartKey,
      orderEndKey,
      orderReplaceKey,
    );
    newTemplate.content = orderTemplateArea.modifiedContent;
    // Add the custom positions var to the var array of the template
    const customOrderVar = {
      value: orderReplaceKey,
      name: 'Document Order',
      preview: orderTemplateArea.contentBetweenKeys[0],
    };
    templateVars.push(customOrderVar);
    const orderTableTemplateString = orderTemplateArea.contentBetweenKeys[0];
    setTemplateOrder(orderTableTemplateString);

    // POSITIONS -------------------------------
    // Your start and end keys
    const positionsStartKey = '%%positionRowStart%%';
    const positionsEndKey = '%%positionRowEnd%%';
    const positionsTemplateAreas = getTemplateAreas(
      newTemplate.content,
      positionsStartKey,
      positionsEndKey,
      'documentPositions',
    );

    newTemplate.content = positionsTemplateAreas.modifiedContent;
    // Add the custom positions var to the var array of the template
    positionsTemplateAreas?.contentBetweenKeys.forEach((obj) => {
      const { key, content } = obj;
      templateVars.push({
        value: key,
        name: 'Document Positions',
        preview: content,
      });
    });
    setTemplatePositions(positionsTemplateAreas.contentBetweenKeys);

    // SKONTO --------------------------------
    const skontoStartKey = '%%discountAreaStart%%';
    const skontoEndKey = '%%discountAreaEnd%%';
    const skontoReplaceKey = '%%documentSkonto%%';
    const skontoTemplateArea = getTemplateArea(
      newTemplate.content,
      skontoStartKey,
      skontoEndKey,
      skontoReplaceKey,
    );
    newTemplate.content = skontoTemplateArea.modifiedContent;
    const customSkontoVar = {
      value: skontoReplaceKey,
      name: 'Document Skonto',
      preview: '',
    };
    templateVars.push(customSkontoVar);
    setTemplateSkonto(
      `${skontoStartKey}${skontoTemplateArea.contentBetweenKeys}${skontoEndKey}`,
    );

    // META FIELDS -------------------------------
    const metaTemplateVars = convertMetaFieldsToTemplateVars(
      templateMeta.metaFields,
    );
    templateVars = templateVars.concat(metaTemplateVars);

    // save template
    setTemplate({ ...newTemplate, vars: templateVars });
    setTemplateChange(templateChange + 1);
  }

  // change templateVars to the dataCollections data
  function prepairTemplateVars(varsToPrepair) {
    if (debug) {
      const valueKeyMap = getTrueTemplateVarValue(dataCollection, true);
      var debugTemplateVars = varsToPrepair;
      const noVarList = [
        '%%documentPositions%%',
        '%%documentPostPositions%%',
        '%%invoicesSection%%',
        '%%orderTable%%',
      ];
      Object.keys(valueKeyMap).forEach((key) => {
        if (
          noVarList.includes(key) ||
          varsToPrepair.find((v) => v.value === key)
        )
          return;
        debugTemplateVars.push({
          name: key,
          preview: 'DEBUG',
          value: key,
        });
      });
      return setTemplate({
        ...template,
        vars: replaceTemplateVars(
          debugTemplateVars,
          valueKeyMap,
          dataCollection.positions || [],
        ),
      });
    }
    if (!varsToPrepair) return;
    const valueKeyMap = getTrueTemplateVarValue(dataCollection, true);
    setTemplate({
      ...template,
      vars: replaceTemplateVars(
        varsToPrepair,
        valueKeyMap,
        dataCollection.positions || [],
      ),
    });
  }

  function handleDynamicElements() {
    if (template.vars.length) {
      const templateSections = [
        'headerP1',
        'headerP2',
        'content',
        'footerP1',
        'footerP2',
      ];
      var updatedTemplate = { ...template };
      templateSections.forEach((section) => {
        // enable deliveryRange or deliveryDate

        if (
          Boolean(
            dataCollection.useDeliveryDateRange ||
            dataCollection.deliveryDateStart,
          )
        ) {
          // remove deliveryDate
          const startKey = '%%deliveryDateAreaStart%%';
          const endKey = '%%deliveryDateAreaEnd%%';
          const replaceKey = '';

          const deliveryDateTemplateArea = getTemplateArea(
            template[section],
            startKey,
            endKey,
            replaceKey,
          );
          updatedTemplate[section] = deliveryDateTemplateArea.modifiedContent;
        } else {
          // remove deliveryDateRange
          const startKey = '%%deliveryDateRangeAreaStart%%';
          const endKey = '%%deliveryDateRangeAreaEnd%%';
          const replaceKey = '';

          const deliveryDateRangeTemplateArea = getTemplateArea(
            template[section],
            startKey,
            endKey,
            replaceKey,
          );
          updatedTemplate[section] =
            deliveryDateRangeTemplateArea.modifiedContent;
        }

        // add or remove the skonto section
        if (Boolean(Number(dataCollection.discount))) {
          // add skonto section
          const skontoContentVars = [
            {
              value: '%%documentSkonto%%',
              name: '',
              preview: templateSkonto,
            },
          ];
          const newContent = TemplateVarSwitcher(
            updatedTemplate[section],
            skontoContentVars,
          );
          updatedTemplate[section] = newContent;
        } else {
          // remove skonto section
          const skontoStartKey = '%%discountAreaStart%%';
          const skontoEndKey = '%%discountAreaEnd%%';
          const skontoReplaceKey = '%%documentSkonto%%';

          const skontoTemplateArea = getTemplateArea(
            updatedTemplate[section],
            skontoStartKey,
            skontoEndKey,
            skontoReplaceKey,
          );
          updatedTemplate[section] = skontoTemplateArea.modifiedContent;
        }
      });
      setTemplate({ ...updatedTemplate });
    }
  }

  useEffect(() => {
    if (template) prepairTemplateVars(template.vars);
  }, [
    debouncedDataCollection,
    templatePositions,
    templateChange,
    templateOrderTable,
    templateInvoices,
    hoaiData,
    config,
  ]);

  useEffect(() => {
    if (!template) return;
    handleDynamicElements();
  }, [debouncedDataCollection]);

  function getTrueTemplateVarValue(data = {}, hasInvoiceArray) {
    const projectLocation = data?.project?.address ?? {};

    // dates
    const dateFormat = config.general.dateOutputFormat;

    const discountDeadline = moment()
      .add(
        data.discountPaymentDeadlineInDays || data.discountDaysToPay || 0,
        'days',
      )
      .format(dateFormat);

    const paymentDeadline = moment()
      .add(data.paymentDeadlineInDays || 0, 'days')
      .format(dateFormat);

    // get paymentMethod
    const { options } = config.invoice.paymentOptions;
    const paymentMethod = options.find(
      (obj) => data.paymentMethod === obj.value,
    );

    // invoices
    function calcAlreadyInvoicedAmount(key) {
      if (!data.previousInvoices) return 0;
      var newSum = 0;
      data.previousInvoices.forEach((element) => {
        newSum += element[key];
      });
      return newSum;
    }

    // positionsBillableSumNetto & Brutto
    let totalBilledNettoSum = 0;
    let totalBilledBruttoSum = 0;

    if (data.orderPositions) {
      totalBilledNettoSum = data.orderPositions.reduce((sum, pos) => {
        const billed = round(parseFloat(pos.billedTotal || 0));
        return sum + billed;
      }, 0);

      totalBilledBruttoSum = data.orderPositions.reduce((sum, pos) => {
        const billed = round(parseFloat(pos.billedTotal));
        const taxRate = round(parseFloat(pos.taxRate));
        const brutto = round(billed * (1 + taxRate / 100));
        return sum + brutto;
      }, 0);
    }

    const alreadyInvoicedNettoAmount = calcAlreadyInvoicedAmount('amount');
    const alreadyInvoicedBruttoAmount =
      calcAlreadyInvoicedAmount('bruttoAmount');
    // only the order-linked portion of each previous invoice (positions with
    // orderId) counts as already invoiced against the order, unless the
    // template includes order-ineffective positions (then the whole invoice
    // counts) — same rule as the generated deduction positions
    // (getInvoiceDeduction).
    const useIneffective = template?.metaData?.useOrderIneffective;
    const orderEffectiveInvoiced = (data.previousInvoices || []).reduce(
      (acc, inv) => {
        const eff = getInvoiceDeduction(inv, useIneffective);
        acc.netto += eff.netto;
        acc.brutto += eff.brutto;
        return acc;
      },
      { netto: 0, brutto: 0 },
    );
    const orderEffectiveInvoicedNettoAmount = orderEffectiveInvoiced.netto;
    const orderEffectiveInvoicedBruttoAmount = orderEffectiveInvoiced.brutto;

    // order
    const orderNettoAmount = data.order?.volume || data.orderPrice;
    const orderBruttoAmount =
      data.order?.taxVolume + data.order?.volume ||
      data.orderTaxAmount + data.orderPrice;
    const orderTaxAmount = data.order?.taxVolume || data.orderTaxAmount;

    // discount
    const discountOrderAmountNetto =
      data.orderPrice * ((data.orderDiscountPercent || 0) / 100);
    const discountOrderAmountTax =
      data.orderTaxAmount * ((data.orderDiscountPercent || 0) / 100);

    // discounted open order
    const discountedOpenOrderNettoAmount =
      orderNettoAmount -
      discountOrderAmountNetto -
      orderEffectiveInvoicedNettoAmount;
    const discountedOpenOrderBruttoAmount =
      orderBruttoAmount -
      (discountOrderAmountNetto + discountOrderAmountTax) -
      orderEffectiveInvoicedBruttoAmount;

    // positions sum template Vars
    var nettoSum = 0;
    var bruttoSum = 0;
    var subtotalNetto = 0;
    if (data.positions) {
      data.positions.forEach((pos) => {
        if (pos.isOptional) return;
        nettoSum += round(pos.price * pos.amount);
        bruttoSum += round(pos.price * pos.amount * (1 + pos.taxRate / 100));
        subtotalNetto += pos.isPostPosition ? 0 : round(pos.price * pos.amount);
      });
    }
    if (!nettoSum && discountedOpenOrderNettoAmount)
      nettoSum = discountedOpenOrderNettoAmount;
    if (!bruttoSum && discountedOpenOrderBruttoAmount)
      bruttoSum = discountedOpenOrderBruttoAmount;

    // HOAI default totals
    let totalHoaiDefaultAmount = 0;
    let totalHoaiDefaultSumNetto = 0;
    let hasAnyHoaiDefault = false;
    if (data.positions) {
      data.positions.forEach((pos) => {
        if (pos.type !== 'hoaiPhase' || pos.isGroup || pos.isOptional) return;
        const rawMax =
          pos.hoaiSiemonPosition?.maxPercent ?? pos.hoaiPosition?.maxPercent;
        const defaultPercent = rawMax != null ? parseFloat(rawMax) : 0;
        if (defaultPercent <= 0) return;
        totalHoaiDefaultAmount += defaultPercent;
        totalHoaiDefaultSumNetto += round(
          (defaultPercent / 100) * parseFloat(pos.price || 0),
        );
        hasAnyHoaiDefault = true;
      });
    }

    const hideCountry =
      config?.general?.omitCountryInDocuments === '1' &&
      config?.companyAddress?.countryCode ===
        data?.recipient?.address?.countryCode;

    // brutto & open amount for payed/not payed
    const bruttoAmount = parseFloat(data.bruttoAmount || 0);
    const openAmount = parseFloat(data.openAmount || 0); //notPayedPositionsSumBrutto
    const amount = parseFloat(data.amount || 0);
    const payedPositionsSumBrutto = bruttoAmount - openAmount;
    const payedPositionsSumNetto =
      amount * (payedPositionsSumBrutto / bruttoAmount); // to get payed netto amount
    const notPayedPositionsSumNetto = amount - payedPositionsSumNetto; // to get not payed netto amount
    const payedPositionsSumTax =
      payedPositionsSumBrutto - payedPositionsSumNetto;
    const notPayedPositionsSumTax = openAmount - notPayedPositionsSumNetto; // to get not payed tax amount

    // ----- ALREADY INVOICED (excluding current one) -----
    let alreadyInvoicedAndPayedSumNetto = 0;
    let alreadyInvoicedAndPayedSumBrutto = 0;
    let alreadyInvoicedAndPayedSumTax = 0;

    if (data.previousInvoices && Array.isArray(data.previousInvoices)) {
      data.previousInvoices.forEach((prev) => {
        // skip the current invoice if it appears here
        if (prev.id === data.id) return;

        const bruttoAmountPrev = parseFloat(prev.bruttoAmount || 0);
        const openAmountPrev = parseFloat(prev.openAmount || 0);
        const amountPrev = parseFloat(prev.amount || 0);

        const payedPositionsSumBruttoPrev = bruttoAmountPrev - openAmountPrev;
        const payedPositionsSumNettoPrev =
          amountPrev * (payedPositionsSumBruttoPrev / bruttoAmountPrev);
        const payedPositionsSumTaxPrev =
          payedPositionsSumBruttoPrev - payedPositionsSumNettoPrev;

        alreadyInvoicedAndPayedSumBrutto += payedPositionsSumBruttoPrev;
        alreadyInvoicedAndPayedSumNetto += payedPositionsSumNettoPrev;
        alreadyInvoicedAndPayedSumTax += payedPositionsSumTaxPrev;
      });
    }

    // ----- ALREADY INVOICED AND NOT PAYED (excluding current one) -----
    let alreadyInvoicedAndNotPayedSumNetto = 0;
    let alreadyInvoicedAndNotPayedSumBrutto = 0;
    let alreadyInvoicedAndNotPayedSumTax = 0;

    if (data.previousInvoices && Array.isArray(data.previousInvoices)) {
      data.previousInvoices.forEach((prev) => {
        // skip the current invoice if it appears here
        if (prev.id === data.id) return;

        const bruttoAmountPrev = parseFloat(prev.bruttoAmount || 0);
        const openAmountPrev = parseFloat(prev.openAmount || 0);
        const amountPrev = parseFloat(prev.amount || 0);

        const payedPositionsSumBruttoPrev = bruttoAmountPrev - openAmountPrev;
        const payedPositionsSumNettoPrev =
          amountPrev * (payedPositionsSumBruttoPrev / bruttoAmountPrev);

        const notPayedPositionsSumBruttoPrev = openAmountPrev;
        const notPayedPositionsSumNettoPrev =
          amountPrev - payedPositionsSumNettoPrev;
        const notPayedPositionsSumTaxPrev =
          notPayedPositionsSumBruttoPrev - notPayedPositionsSumNettoPrev;

        alreadyInvoicedAndNotPayedSumBrutto += notPayedPositionsSumBruttoPrev;
        alreadyInvoicedAndNotPayedSumNetto += notPayedPositionsSumNettoPrev;
        alreadyInvoicedAndNotPayedSumTax += notPayedPositionsSumTaxPrev;
      });
    }

    const totalInvoicedAndNotPayedSumBrutto =
      alreadyInvoicedAndNotPayedSumBrutto + bruttoSum;

    const totalInvoicedBruttoAmount = alreadyInvoicedBruttoAmount + bruttoSum;
    const totalInvoicedTaxAmount =
      alreadyInvoicedBruttoAmount -
      alreadyInvoicedNettoAmount +
      (bruttoSum - nettoSum);
    const totalInvoicedNettoAmount = alreadyInvoicedNettoAmount + nettoSum;

    // stand today (sum(amount+billedAmount))
    function calcTotalPositionBillableUnits(key1, key2) {
      if (!data.positions) return 0;
      return data.positions
        .filter((pos) => pos.type === 'hoaiPhase')
        .reduce(
          (sum, pos) =>
            sum + (parseFloat(pos[key1] || 0) + parseFloat(pos[key2] || 0)),
          0,
        );
    }
    // stand today (sum(amount+billedAmount))
    function calcTotalPositionOrderedUnits(key1) {
      if (!data.positions) return 0;
      return data.positions
        .filter((pos) => pos.type === 'hoaiPhase')
        .reduce(
          (sum, pos) => sum + parseFloat(pos[key1] || pos['amount'] || 0),
          0,
        );
    }

    const totalPositionBillableUnits = calcTotalPositionBillableUnits(
      'amount',
      'billedAmount',
    );
    const positionOrderedUnits = calcTotalPositionOrderedUnits('totalAmount');

    // (hoaiTypes)
    let surcharge = null;
    let timeFee = null;
    let specialServices = null;
    let additionalServices = null;
    let incidentalCosts = null;

    if (data.positions) {
      surcharge = data?.positions.find((pos) => pos.hoaiType === 'surcharge');
      timeFee = data?.positions.find((pos) => pos.hoaiType === 'timeFee');
      specialServices = data?.positions.find(
        (pos) => pos.hoaiType === 'specialServices',
      );
      additionalServices = data?.positions.find(
        (pos) => pos.hoaiType === 'additionalServices',
      );
      incidentalCosts = data?.positions.find(
        (pos) => pos.hoaiType === 'incidentalCosts',
      );
      /*buildingSubstance = data?.positions.find(
        (pos) => pos.hoaiType === 'buildingSubstance'
      );
      tgaShare = data?.positions.find((pos) => pos.hoaiType === 'tgaShare');*/
    }

    // get all the metaFields as valueKeyMap entries
    const metaValueKeyMap = convertMetaFieldsToValueKeyMap(
      config,
      data,
      template.metaData?.metaFields,
    );

    // render all position tables
    var positionsValueKeyMap = {};

    templatePositions.forEach((obj) => {
      const { key, content } = obj;
      positionsValueKeyMap[key] = {
        value: generateRenderedTemplatePosition(
          content,
          data.positions,
          template.vars,
          hoaiData,
        ),
        fallback: '',
      };
    });

    const hoaiTypeValueKeyMap = generateHoaiValueKeyMap(
      data.positions,
      hoaiData,
    );

    // order effective & ineffective sums
    var orderEffective = { netto: 0, tax: 0, brutto: 0 };

    const orderEffectivePositions = data.positions?.filter(
      (pos) => pos.isOrderPosition,
    );
    orderEffectivePositions?.forEach((element) => {
      orderEffective.netto += round(element.price * element.amount);
      orderEffective.tax += round(
        round(element.price * element.amount) * (element.taxRate / 100),
      );
      orderEffective.brutto += round(
        round(element.price * element.amount) * (1 + element.taxRate / 100),
      );
    });
    var orderIneffective = { netto: 0, tax: 0, brutto: 0 };
    const orderIneffectivePositions = data.positions?.filter(
      (pos) => !pos.isOrderPosition,
    );
    orderIneffectivePositions?.forEach((element) => {
      orderIneffective.netto += round(element.price * element.amount);
      orderIneffective.tax += round(
        round(element.price * element.amount) * (element.taxRate / 100),
      );
      orderIneffective.brutto += round(
        round(element.price * element.amount) * (1 + element.taxRate / 100),
      );
    });

    const employeeWorkContractValueKeyMap =
      generateWorkContractValueKeyMap(data);

    // assign values to the right key/placeholder
    const valueKeyMap = {
      ...employeeWorkContractValueKeyMap,
      ...hoaiTypeValueKeyMap,
      ...metaValueKeyMap,
      ...positionsValueKeyMap,
      /*//Somit Honorar für gelieferte Leistungen  ------  ISNT it for each position? should be in there?
      '%%somitHonorar%%': {
        value: numberToString(
          totalPositionBillableUnits * hoaiData?.baseHonorarium,
          2
        ),
        fallback: '',
      },*/
      '%%leitwegId%%': {
        value: data.leitwegId || '',
        fallback: '',
      },
      '%%positionsSumNettoOrderEffective%%': {
        value: numberToString(orderEffective.netto),
        fallback: '',
        noHighlight: true,
      },
      '%%positionsSumTaxOrderEffective%%': {
        value: numberToString(orderEffective.tax),
        fallback: '',
        noHighlight: true,
      },
      '%%positionsSumBruttoOrderEffective%%': {
        value: numberToString(orderEffective.brutto),
        fallback: '',
        noHighlight: true,
      },
      '%%positionsSumNettoOrderIneffective%%': {
        value: numberToString(orderIneffective.netto),
        fallback: '',
        noHighlight: true,
      },
      '%%positionsSumTaxOrderIneffective%%': {
        value: numberToString(orderIneffective.tax),
        fallback: '',
        noHighlight: true,
      },
      '%%positionsSumBruttoOrderIneffective%%': {
        value: numberToString(orderIneffective.brutto),
        fallback: '',
        noHighlight: true,
      },

      '%%recipientAddition%%': {
        value: dataCollection.recipientAddition || '',
        fallback: '',
      },
      '%%hasRecipientAddition%%': {
        value: Boolean(dataCollection.recipientAddition)
          ? 'recipient-addition'
          : '',
        fallback: '',
      },
      // '%%hasPostPositions%%': {
      //   value: 'post-positions',
      //   fallback: '',
      // },
      '%%invoicesSection%%': {
        value: hasInvoiceArray
          ? generateRenderedTemplateInvoices(templateInvoices)
          : '',
        fallback: '',
      },
      '%%orderEffectiveInvoicedNettoAmount%%': {
        value: numberToString(orderEffectiveInvoicedNettoAmount),
        fallback: 0,
      },
      '%%orderEffectiveInvoicedTaxAmount%%': {
        value: numberToString(
          orderEffectiveInvoicedBruttoAmount -
            orderEffectiveInvoicedNettoAmount,
        ),
        fallback: 0,
      },
      '%%orderEffectiveInvoicedBruttoAmount%%': {
        value: numberToString(orderEffectiveInvoicedBruttoAmount),
        fallback: 0,
      },
      '%%discountedOpenOrderNettoAmount%%': {
        value: numberToString(discountedOpenOrderNettoAmount),
        fallback: 0,
      },
      '%%discountedOpenOrderTaxAmount%%': {
        value: numberToString(
          discountedOpenOrderBruttoAmount - discountedOpenOrderNettoAmount,
        ),
        fallback: 0,
      },
      '%%discountedOpenOrderBruttoAmount%%': {
        value: numberToString(discountedOpenOrderBruttoAmount),
        fallback: 0,
      },
      '%%alreadyInvoicedNettoAmount%%': {
        value: numberToString(alreadyInvoicedNettoAmount),
        fallback: 0,
      },
      '%%alreadyInvoicedTaxAmount%%': {
        value: numberToString(
          alreadyInvoicedBruttoAmount - alreadyInvoicedNettoAmount,
        ),
        fallback: 0,
      },
      '%%alreadyInvoicedBruttoAmount%%': {
        value: numberToString(alreadyInvoicedBruttoAmount),
        fallback: 0,
      },

      '%%orderNr%%': {
        value: data.orderNr,
        fallback: t('Empty'),
      },
      '%%orderTitle%%': {
        value: data.orderTitle,
        fallback: t('Empty'),
      },
      '%%orderDiscountPercent%%': {
        value: numberToString(data.orderDiscountPercent || 0, 1),
        fallback: 0,
        noHighlight: true,
      },
      '%%orderNettoAmount%%': {
        value: numberToString(orderNettoAmount),
        fallback: t('Empty'),
      },
      '%%orderBruttoAmount%%': {
        value: numberToString(orderBruttoAmount),
        fallback: t('Empty'),
      },
      '%%orderTaxAmount%%': {
        value: numberToString(orderTaxAmount),
        fallback: t('Empty'),
      },
      '%%orderDiscountNettoAmount%%': {
        value: numberToString(discountOrderAmountNetto),
        fallback: t('Empty'),
      },
      '%%orderDiscountTaxAmount%%': {
        value: numberToString(discountOrderAmountTax),
        fallback: t('Empty'),
      },
      '%%orderDiscountBruttoAmount%%': {
        value: numberToString(
          discountOrderAmountNetto + discountOrderAmountTax,
        ),
        fallback: t('Empty'),
      },
      '%%paymentMethod%%': {
        value: t(paymentMethod?.documentLabel),
        fallback: t('Empty'),
      },
      '%%paymentDeadlineDate%%': {
        value: paymentDeadline,
        fallback: '01.01.2022',
      },
      '%%paymentDeadlineDays%%': {
        value: data.paymentDeadlineInDays,
        fallback: '01.01.2022',
      },
      '%%positionsSumBrutto%%': {
        value: numberToString(bruttoSum),
        fallback: '0.00',
        noHighlight: true,
      },
      '%%positionsSumNetto%%': {
        value: numberToString(nettoSum),
        fallback: '0.00',
        noHighlight: true,
      },
      '%%positionsSubtotalNetto%%': {
        value: numberToString(subtotalNetto),
        fallback: '0.00',
        noHighlight: true,
      },
      '%%positionsSumTax%%': {
        value: numberToString(bruttoSum - nettoSum),
        fallback: '0.00',
        noHighlight: true,
      },
      '%%positionsHoaiDefaultAmount%%': {
        value: hasAnyHoaiDefault
          ? `${numberToString(totalHoaiDefaultAmount, 2)} %`
          : '',
        fallback: '',
        noHighlight: true,
      },
      '%%hoaiDefaultPhasesNetto%%': {
        value: hasAnyHoaiDefault
          ? numberToString(totalHoaiDefaultSumNetto, 2)
          : '',
        fallback: '',
        noHighlight: true,
      },
      '%%documentDate%%': {
        value: moment(data.date).format(dateFormat),
        fallback: t('Empty'),
      },
      '%%letterContent%%': {
        value: data.content,
        fallback: t('Empty'),
      },
      '%%documentNr%%': {
        value: data.nr,
        fallback: t('*AUTOMATIC*'),
      },
      '%%documentTitle%%': {
        value: data.title || data.subject,
        fallback: t('Empty'),
      },
      '%%documentIntroduction%%': {
        value: data.introduction || '',
        fallback: '',
      },
      '%%documentNote%%': {
        value: data.note,
        fallback: '',
      },
      '%%documentValidUntil%%': {
        value: moment(data.validUntil).format(dateFormat),
        fallback: t('Empty'),
      },
      '%%documentContactPersonSalutation%%': {
        value: data.contactPerson ? data.contactPerson.salutation : null,
        fallback: t('Empty'),
      },
      '%%documentContactPersonFirstName%%': {
        value: data.contactPerson ? data.contactPerson.firstName : null,
        fallback: t('Empty'),
      },
      '%%documentContactPersonLastName%%': {
        value: data.contactPerson ? data.contactPerson.lastName : null,
        fallback: t('Empty'),
      },
      '%%documentContactPersonEmail%%': {
        value: data.contactPerson ? data.contactPerson.email : null,
        fallback: t('Empty'),
      },
      '%%documentContactPersonTelephoneNumber%%': {
        value: data.contactPerson ? data.contactPerson.phone : null,
        fallback: t('Empty'),
      },
      '%%recipientClientNumber%%': {
        value: data.recipient ? data.recipient.nr : '',
        fallback: t('Empty'),
      },
      '%%recipientCompanyName%%': {
        value: data.recipient ? data.recipient.name : '',
        fallback: t('Empty'),
      },
      '%%recipientCompanyNameShort%%': {
        value: data.recipient ? data.recipient.nameShort : '',
        fallback: t('Empty'),
      },
      '%%recipientWebsiteUrl%%': {
        value: data.recipient ? data.recipient.url : '',
        fallback: t('Empty'),
      },
      '%%recipientIban%%': {
        value: data.recipient ? data.recipient.iban : '',
        fallback: t('Empty'),
      },
      '%%recipientInvoiceMail%%': {
        value: data.recipient ? data.recipient.invoiceMail : '',
        fallback: t('Empty'),
      },
      '%%recipientContactMail%%': {
        value: data.recipient ? data.recipient.mail : '',
        fallback: t('Empty'),
      },
      '%%recipientPhoneNumber%%': {
        value: data.recipient ? data.recipient.phone : '',
        fallback: t('Empty'),
      },
      '%%recipientStreet%%': {
        value: data.recipient ? data.recipient?.address?.street : '',
        fallback: t('Empty'),
      },
      '%%recipientHouseNumber%%': {
        value: data.recipient ? data.recipient?.address?.houseNumber : '',
        fallback: '',
      },
      '%%recipientZipCode%%': {
        value: data.recipient ? data.recipient?.address?.zipCode : '',
        fallback: '',
      },
      '%%recipientCity%%': {
        value: data.recipient ? data.recipient?.address?.city : '',
        fallback: t('Empty'),
      },
      '%%recipientCountry%%': {
        value:
          data.recipient && !hideCountry
            ? data.recipient?.address?.country
            : '',
        fallback: '',
      },
      '%%recipientAddressAddition%%': {
        value: data.recipient ? data.recipient?.address?.addition : '',
        fallback: '',
      },
      '%%recipientSalutation%%': {
        value: data.recipient ? data.recipient.salutation : '',
        fallback: t('Empty'),
      },
      '%%recipientFirstName%%': {
        value: data.recipient ? data.recipient.firstName : '',
        fallback: t('Empty'),
      },

      '%%recipientPhoneNumber%%': {
        value: data.recipient
          ? getPrimaryContactData(
              data.recipient.telephoneNumbers,
              'phoneNumber',
            )
          : '',
        fallback: t('Empty'),
      },
      '%%recipientInvoiceMail%%': {
        value: data.recipient
          ? getPrimaryContactData(
              data.recipient.emails,
              'emailAddress',
              'isInvoiceMail',
            )
          : '',
        fallback: t('Empty'),
      },
      '%%recipientContactMail%%': {
        value: data.recipient
          ? getPrimaryContactData(data.recipient.emails, 'emailAddress')
          : '',
        fallback: t('Empty'),
      },
      '%%recipientMail%%': {
        value: data.recipient
          ? getPrimaryContactData(data.recipient.emails, 'emailAddress')
          : '',
        fallback: t('Empty'),
      },
      '%%recipientLastName%%': {
        value: data.recipient ? data.recipient.lastName : '',
        fallback: t('Empty'),
      },
      '%%cashDiscountRate%%': {
        value: numberToString(data.discountRate, 2),
        fallback: '',
      },
      '%%discountedPrice%%': {
        value: numberToString(
          bruttoSum - round((data.discountRate / 100) * bruttoSum),
        ),
        fallback: '',
      },
      '%%cashDiscountDaysToPay%%': {
        value: data.discountDaysToPay ?? data.discountPaymentDeadlineInDays,
        fallback: '',
      },
      '%%documentDeliveryStartDate%%': {
        value: moment(data.deliveryDateStart).format(dateFormat),
        fallback: '',
      },
      '%%documentDeliveryEndDate%%': {
        value: moment(data.deliveryDateEnd).format(dateFormat),
        fallback: '',
      },
      '%%cashDiscountDeadline%%': {
        value: discountDeadline,
        fallback: '',
      },
      '%%hasProjectAddressClassName%%': {
        value:
          data.project && data.project.address ? 'has-project-address' : '',
        fallback: '',
        noHighlight: true,
      },
      '%%hasProjectClassName%%': {
        value: data.project ? 'has-project' : '',
        fallback: '',
        noHighlight: true,
      },
      '%%projectNr%%': {
        value: data.project ? data.project.nr : '',
        fallback: '',
      },
      '%%projectName%%': {
        value: data.project ? data.project.name : '',
        fallback: '',
      },
      '%%projectExternalNr%%': {
        value: data.project ? data.project.externalNr : '',
        fallback: '',
        noHighlight: true,
      },
      '%%projectStreet%%': {
        value: projectLocation.street,
        fallback: '',
      },
      '%%projectHouseNumber%%': {
        value: projectLocation.houseNumber,
        fallback: '',
      },
      '%%projectZipCode%%': {
        value: projectLocation.zipCode,
        fallback: '',
      },
      '%%projectCity%%': {
        value: projectLocation.city,
        fallback: '',
      },
      '%%positionsBillableSumNetto%%': {
        value: numberToString(totalBilledNettoSum + nettoSum, 2),
        fallback: '',
        noHighlight: true,
      },
      '%%positionsBillableSumBrutto%%': {
        value: numberToString(totalBilledBruttoSum + bruttoSum, 2),
        fallback: '',
        noHighlight: true,
      },
      '%%positionsBillableSumTax%%': {
        value: numberToString(
          totalBilledBruttoSum + bruttoSum - (totalBilledNettoSum + nettoSum),
          2,
        ),
        fallback: '',
        noHighlight: true,
      },
      '%%payedPositionsSumBrutto%%': {
        value: numberToString(payedPositionsSumBrutto, 2), //states 0 if both values are same
        fallback: '',
        noHighlight: true,
      },
      '%%notPayedPositionsSumBrutto%%': {
        value: numberToString(openAmount, 2),
        fallback: '',
        noHighlight: true,
      },
      '%%payedPositionsSumNetto%%': {
        value: numberToString(payedPositionsSumNetto, 2),
        fallback: '',
        noHighlight: true,
      },
      '%%notPayedPositionsSumNetto%%': {
        value: numberToString(notPayedPositionsSumNetto, 2),
        fallback: '',
        noHighlight: true,
      },
      // '%%payedPositionsSumTax%%': {
      //   value: numberToString(payedPositionsSumTax, 2),
      //   fallback: '',
      // },
      // '%%notPayedPositionsSumTax%%': {
      //   value: numberToString(notPayedPositionsSumTax, 2),
      //   fallback: '',
      // },
      '%%alreadyInvoicedAndPayedSumBrutto%%': {
        value: numberToString(alreadyInvoicedAndPayedSumBrutto, 2),
        fallback: '',
        noHighlight: true,
      },
      '%%alreadyInvoicedAndNotPayedSumBrutto%%': {
        value: numberToString(alreadyInvoicedAndNotPayedSumBrutto, 2),
        fallback: '',
        noHighlight: true,
      },
      '%%alreadyInvoicedAndPayedSumNetto%%': {
        value: numberToString(alreadyInvoicedAndPayedSumNetto, 2),
        fallback: '',
        noHighlight: true,
      },
      '%%alreadyInvoicedAndNotPayedSumNetto%%': {
        value: numberToString(alreadyInvoicedAndNotPayedSumNetto, 2),
        fallback: '',
        noHighlight: true,
      },

      // For current invoice (Summe) row
      '%%totalInvoicedNettoAmount%%': {
        value: numberToString(totalInvoicedNettoAmount, 2), //(Betrag. netto)
        fallback: '0.00',
        noHighlight: true,
      },
      '%%totalInvoicedTaxAmount%%': {
        value: numberToString(totalInvoicedTaxAmount, 2), //(USt)
        fallback: '0.00',
        noHighlight: true,
      },
      '%%totalInvoicedBruttoAmount%%': {
        value: numberToString(totalInvoicedBruttoAmount, 2), //(Betrag. brutto)
        fallback: '0.00',
        noHighlight: true,
      },
      '%%totalInvoicedAndPayedSumBrutto%%': {
        value: numberToString(alreadyInvoicedAndPayedSumBrutto, 2), // becuase current invoice will never have paid brutto amount (betrag. begl)
        fallback: '0.00',
        noHighlight: true,
      },
      '%%totalInvoicedAndNotPayedSumBrutto%%': {
        value: numberToString(totalInvoicedAndNotPayedSumBrutto, 2), // INCLUDING the current invoice. (Offener Betrag)
        fallback: '0.00',
        noHighlight: true,
      },
      // to hide if there is no additional services etc
      '%%hasSurcharge%%': {
        value: surcharge ? 'hasSurcharge' : 'hideRow',
        fallback: '',
        noHighlight: true,
      },
      '%%hasTimeFee%%': {
        value: timeFee ? 'hasTimeFee' : 'hideRow',
        fallback: '',
        noHighlight: true,
      },
      '%%hasSpecialServices%%': {
        value: specialServices ? 'hasSpecialServices' : 'hideRow',
        fallback: '',
        noHighlight: true,
      },
      '%%hasAdditionalServices%%': {
        value: additionalServices ? 'hasAdditionalServices' : 'hideRow',
        fallback: '',
        noHighlight: true,
      },
      '%%hasIncidentalCosts%%': {
        value: incidentalCosts ? 'hasIncidentalCosts' : 'hideRow',
        fallback: '',
        noHighlight: true,
      },
      '%%hasVat%%': {
        value: hoaiData?.vat ? 'hasVat' : 'hideRow',
        fallback: '',
        noHighlight: true,
      },
      '%%vatAmount%%': {
        value: numberToString(hoaiData?.vat, 2),
        fallback: '',
        noHighlight: true,
      },
      '%%vat%%': {
        value: numberToString(hoaiData?.hoaiData?.vatAmount, 2),
        fallback: '',
        noHighlight: true,
      },
      '%%vatFinal%%': {
        value: numberToString(
          (hoaiData?.hoaiData?.vatAmount ?? 0) +
            (hoaiData?.hoaiData?.nettoHonorarium ?? 0),
          2,
        ),
        fallback: '',
        noHighlight: true,
      },
    };

    // Insert surcharge and other HOAI types into valueKeyMap if they exist
    if (surcharge) {
      valueKeyMap['%%surchargeAmount%%'] = {
        value: numberToString(surcharge.amount, 2),
        fallback: '',
      };
      valueKeyMap['%%surchargePrice%%'] = {
        value: numberToString(surcharge.price, 2),
        fallback: '',
      };
      valueKeyMap['%%surchargeFinal%%'] = {
        value: numberToString(
          surcharge.price + surcharge.amount * surcharge.price,
          2,
        ),
        fallback: '',
      };
      valueKeyMap['%%surcharge%%'] = {
        value: numberToString(surcharge.amount * surcharge.price, 2),
        fallback: '',
      };
      valueKeyMap['%%surchargeDescription%%'] = {
        value: surcharge.description || '',
        fallback: '',
      };
    }

    if (timeFee) {
      valueKeyMap['%%timeFeeAmount%%'] = {
        value: numberToString(timeFee.amount, 2),
        fallback: '',
      };
      valueKeyMap['%%timeFeePrice%%'] = {
        value: numberToString(timeFee.price, 2),
        fallback: '',
      };
      valueKeyMap['%%timeFeeFinal%%'] = {
        value: numberToString(
          timeFee.price + timeFee.amount * timeFee.price,
          2,
        ),
        fallback: '',
      };
      valueKeyMap['%%timeFee%%'] = {
        value: numberToString(timeFee.amount * timeFee.price, 2),
        fallback: '',
      };
      valueKeyMap['%%timeFeeDescription%%'] = {
        value: timeFee.description || '',
        fallback: '',
      };
    }

    if (specialServices) {
      valueKeyMap['%%specialServicesAmount%%'] = {
        value: numberToString(specialServices.amount, 2),
        fallback: '',
      };
      valueKeyMap['%%specialServicesPrice%%'] = {
        value: numberToString(specialServices.price, 2),
        fallback: '',
      };
      valueKeyMap['%%specialServicesFinal%%'] = {
        value: numberToString(
          specialServices.price +
            specialServices.amount * specialServices.price,
          2,
        ),
        fallback: '',
      };
      valueKeyMap['%%specialServices%%'] = {
        value: numberToString(totalInvoicedAndNotPayedSumBrutto, 2),
        fallback: '',
      };
      valueKeyMap['%%specialServicesDescription%%'] = {
        value: specialServices.description || '',
        fallback: '',
      };
    }

    if (additionalServices) {
      valueKeyMap['%%additionalServicesAmount%%'] = {
        value: numberToString(additionalServices.amount, 2),
        fallback: '',
      };
      valueKeyMap['%%additionalServicesPrice%%'] = {
        value: numberToString(additionalServices.price, 2),
        fallback: '',
      };
      valueKeyMap['%%additionalServicesFinal%%'] = {
        value: numberToString(
          additionalServices.price +
            additionalServices.amount * additionalServices.price,
          2,
        ),
        fallback: '',
      };
      valueKeyMap['%%additionalServices%%'] = {
        value: numberToString(
          additionalServices.amount * additionalServices.price,
          2,
        ),
        fallback: '',
      };
      valueKeyMap['%%additionalServicesDescription%%'] = {
        value: additionalServices.description || '',
        fallback: '',
      };
    }

    if (incidentalCosts) {
      valueKeyMap['%%incidentalCostsAmount%%'] = {
        value: numberToString(incidentalCosts.amount, 2),
        fallback: '',
      };
      valueKeyMap['%%incidentalCostsPrice%%'] = {
        value: numberToString(incidentalCosts.price, 2),
        fallback: '',
      };
      valueKeyMap['%%incidentalCostsFinal%%'] = {
        value: numberToString(
          incidentalCosts.price +
            incidentalCosts.amount * incidentalCosts.price,
          2,
        ),
        fallback: '',
      };
      valueKeyMap['%%incidentalCosts%%'] = {
        value: numberToString(
          incidentalCosts.amount * incidentalCosts.price,
          2,
        ),
        fallback: '',
      };
      valueKeyMap['%%incidentalCostsDescription%%'] = {
        value: incidentalCosts.description || '',
        fallback: '',
      };
    }

    const orderTable = {
      value: dataCollection.showOrderPositions
        ? generateRenderedOrderTable(
            templateOrderTable,
            dataCollection,
            template.vars,
            valueKeyMap,
            hoaiData,
          )
        : '',
      fallback: '',
    };

    valueKeyMap['%%orderTable%%'] = orderTable;
    return valueKeyMap;
  }

  function getPrimaryContactData(arr, key, validationKey = 'isPrimary') {
    if (!arr) return undefined;
    const primaryContactData = arr.find((obj) => Boolean(obj[validationKey]));
    if (!primaryContactData) return undefined;
    return key ? primaryContactData[key] : primaryContactData;
  }

  function generateRenderedTemplateInvoices(invoiceTemplate) {
    if (
      !invoiceTemplate ||
      !dataCollection?.previousInvoices ||
      !dataCollection?.previousInvoices.length
    )
      return '';

    // get invoice loop part
    const invoicesStartKey = '%%invoicesStart%%';
    const invoicesEndKey = '%%invoicesEnd%%';
    const invoicesReplaceKey = '%%invoices%%';
    const invoicesTemplateArea = getTemplateArea(
      invoiceTemplate,
      invoicesStartKey,
      invoicesEndKey,
      invoicesReplaceKey,
    );
    const baseTemplate = invoicesTemplateArea.modifiedContent;
    const invoiceLoopTemplate = invoicesTemplateArea.contentBetweenKeys[0];

    var renderedInvoices = '';

    const sortedInvoices = [...(dataCollection?.previousInvoices || [])].sort(
      (a, b) =>
        (a.nr || '').localeCompare(b.nr || '', undefined, { numeric: true }),
    );

    sortedInvoices.forEach((invoice) => {
      const valueKeyMap = getTrueTemplateVarValue(invoice, false);

      const { positions, showPositions } = invoice;

      var renderedTemplate = invoiceLoopTemplate;

      if (showPositions) {
        //===== NORMAL POSITIONS =====
        const positionsStartKey = '%%positionRowStart%%';
        const positionsEndKey = '%%positionRowEnd%%';
        const positionsReplaceKey = 'invoicePositions';

        const positionsTemplateAreas = getTemplateAreas(
          renderedTemplate,
          positionsStartKey,
          positionsEndKey,
          positionsReplaceKey,
        );

        renderedTemplate = positionsTemplateAreas.modifiedContent;

        // render positionstables
        positionsTemplateAreas.contentBetweenKeys.forEach((obj) => {
          const { key, content } = obj;
          // Render them
          const renderedPositions = generateRenderedTemplatePosition(
            content,
            positions,
            template.vars,
            hoaiData,
          );

          // Replace
          renderedTemplate = renderedTemplate.replace(key, renderedPositions);
        });
      } else {
        // No positions: remove the tables
        const positionsTableStartKey = '%%invoicePositionsTableStart%%';
        const positionsTableEndKey = '%%invoicePositionsTableEnd%%';
        const removedPositionsTableArea = getTemplateArea(
          renderedTemplate,
          positionsTableStartKey,
          positionsTableEndKey,
          '',
        );
        renderedTemplate = removedPositionsTableArea.modifiedContent;
      }

      renderedInvoices += TemplateVarSwitcher(
        renderedTemplate,
        replaceTemplateVars(template.vars, valueKeyMap),
      );
    });

    const valueKeyMap = getTrueTemplateVarValue(dataCollection, false);

    // render baseTemplate
    const renderedBaseTemplate = TemplateVarSwitcher(
      baseTemplate,
      replaceTemplateVars(template.vars, valueKeyMap),
    );

    const renderdInvoiceSection = renderedBaseTemplate.replace(
      '%%invoices%%',
      renderedInvoices,
    );

    return renderdInvoiceSection;
  }

  return (
    <Box
      className="w-full h-full flex flex-col  justify-center items-center"
      sx={{
        ul: {
          listStyle: 'disc',
        },
        '& .templateVar': {
          display: 'inline',
          animation: 'templateVarChange 5s ease',
          '& p, h1, h2, h3, h4, td': {
            animation: 'templateVarChange 5s ease',
          },
        },
        '@keyframes templateVarChange': {
          '0%': {
            color: 'red',
          },
          '100%': {
            color: 'initial',
          },
        },
        '& .templateVarFallback': {
          color: 'red',
        },
      }}
    >
      {templateWithInlineAttachments !== null && (
        <TemplatePreview
          data={templateWithInlineAttachments}
          paginate={false}
          fit
          className="flex h-full justify-center items-center"
        />
      )}

      {/* Attachment Section */}
      {showAttachments && attachments && attachments.length > 0 && (
        <Box
          className="w-full mt-4 p-4"
          sx={{
            borderTop: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            maxWidth: width,
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
            {t('Attachments')} ({attachments.length})
          </Typography>
          <ExoAttachments attachments={attachments} />
        </Box>
      )}
    </Box>
  );
};

function getTemplateArea(content, startKey, endKey, replacerKey) {
  // Use a global regular expression to match all areas between startKey and endKey
  const regex = new RegExp(`${startKey}(.*?)${endKey}`, 'gs'); // 's' flag allows dot (.) to match newline characters
  let contentBetweenKeys = [];

  // Replace all matches and store the matched content between the keys
  const modifiedContent = content.replace(regex, (match, group1) => {
    contentBetweenKeys.push(group1); // Store the content between start and end keys
    return replacerKey; // Replace the matched area with replacerKey
  });

  return { modifiedContent, contentBetweenKeys };
}

// allow multiple areas
function getTemplateAreas(content, startKey, endKey, replacerKey) {
  // Escape keys for regex safety
  const escapedStart = startKey.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const escapedEnd = endKey.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  // Match all occurrences, including multiline ones
  const regex = new RegExp(`${escapedStart}(.*?)${escapedEnd}`, 'gs');

  const contentBetweenKeys = [];
  let index = 0;

  const modifiedContent = content.replace(regex, (match, group1) => {
    const trimmed = group1.trim();
    const key = `%%${replacerKey}-${++index}%%`;
    contentBetweenKeys.push({ key, content: trimmed });
    return key;
  });

  return { modifiedContent, contentBetweenKeys };
}

function groupSumWrapper(number) {
  return `<b style="opacity: 0.5; padding-right: 8px;">[${numberToString(
    number,
  )} €]</b>`;
}

function generateRenderedTemplatePosition(
  template,
  positions,
  globalTemplateVars,
  hoaiData,
) {
  if (!template) return '';
  var renderedTemplatePositions = '';
  if (positions) {
    let totalPositionOrderedUnits = 0;
    positions.forEach((element) => {
      const {
        type,
        amount,
        price,
        orderedPrice,
        billedTotal,
        billedAmount,
        totalAmount,
        isPostPosition,
      } = element;

      const numAmount = parseFloat(amount || 0);
      const numPrice = parseFloat(price || 0);
      const numOrderedPrice = parseFloat(orderedPrice ?? price ?? 0);
      const numTotalAmount = parseFloat(totalAmount || 0);
      const numBilledTotal = parseFloat(billedTotal || 0);
      const numBilledAmount = parseFloat(billedAmount || 0);

      // beauftragt in €
      const positionOrderSumNetto = numberToString(
        numOrderedPrice * numTotalAmount,
        2,
      );

      var renderedAmount = numberToString(amount, 2);

      // abrechenbar in %
      let PosBillablePercentage = '';
      const denominator = numTotalAmount * numOrderedPrice;

      if (denominator > 0) {
        const ratio =
          ((numBilledTotal + numAmount * numPrice) / denominator) * 100;
        PosBillablePercentage = numberToString(ratio, 2);
      } else {
        PosBillablePercentage = numberToString(100, 2); // show 100%
      }

      // abrechenbar in €
      const abrechenbarPriceNum = numPrice * numAmount + numBilledTotal;
      var abrechenbarPrice = numberToString(abrechenbarPriceNum, 2);

      // bereits verrechnet in €
      var renderedBilledTotal = numberToString(billedTotal, 2);

      // Numeric quantities behind %%positionBilledUnits%% / %%positionBillableUnits%%.
      const billedUnitsNum = numBilledAmount;
      const billableUnitsNum =
        element.hoaiId &&
        numAmount === 1 &&
        !element.isFlat &&
        element.hoaiPosition?.type !== 'flat' &&
        hoaiData?.baseHonorarium
          ? (numPrice + numBilledTotal) / hoaiData.baseHonorarium
          : numBilledAmount + numAmount;

      // Divisors for the ...PricePerUnit vars. For percentage positions the quantity is
      // constant across both states and the reference value is what differs, so divide by
      // the position's own percentage. Mirrors replaceModelPositions() in the backend.
      const isPercentType = type === 'percent' || type === 'percentEditable';
      const billedUnitsDivisor = isPercentType ? numAmount : billedUnitsNum;
      const billableUnitsDivisor = isPercentType ? numAmount : billableUnitsNum;

      if (!isPostPosition) {
        totalPositionOrderedUnits += numTotalAmount;
        // console.log(numTotalAmount);
      }

      if (type === 'hoaiPhase') {
        renderedAmount = `${numberToString(numAmount * 100, 2)} %`;
      } else if (type === 'percent' || type === 'percentEditable') {
        renderedAmount = `${numberToString(parseFloat(amount) * 100, 2)} %`;
      }

      // HOAI default amount lookup from the embedded hoaiPosition/hoaiSiemonPosition
      let positionHoaiDefaultAmount = '';
      let positionHoaiDefaultSumNetto = '';
      if (type === 'hoaiPhase' && !element.isGroup) {
        const rawMax =
          element.hoaiSiemonPosition?.maxPercent ??
          element.hoaiPosition?.maxPercent;
        const defaultPercent = rawMax != null ? parseFloat(rawMax) : 0;
        if (defaultPercent > 0) {
          positionHoaiDefaultAmount = `${numberToString(defaultPercent, 2)} %`;
          positionHoaiDefaultSumNetto = numberToString(
            round((defaultPercent / 100) * numPrice),
            2,
          );
        }
      }

      const positionVarsMap = {
        // Stand current (sum(amount + billedAmount))
        /*'%%totalPositionBillableUnits%%': {
          value: numberToString(totalPositionBillableUnits * 100, 2),
          fallback: '',
        },
*/
        //ordered amount in units or percent
        '%%positionIsGroup%%': {
          value: element.isGroup ? 'group-position' : '',
          fallback: '',
          noHighlight: true,
        },
        '%%positionOrderedUnits%%': {
          value: element.isGroup
            ? ''
            : element.hoaiId
              ? numberToString(element.totalAmount * 100, 2) + ' %'
              : numberToString(element.totalAmount, 2) +
                ` ${element.unit || ''}`,
          fallback: '',
        },
        '%%positionPosition%%': {
          value: element.position,
          fallback: '',
        },
        '%%positionName%%': {
          value: element.name,
          fallback: '',
        },
        '%%positionAmount%%': {
          value: element.isGroup ? '' : renderedAmount,
          fallback: '',
        },
        '%%positionUnit%%': {
          value: element.isGroup ? '' : element.unit,
          fallback: '',
        },
        '%%positionPrice%%': {
          value: element.isGroup ? '' : numberToString(element.price),
          fallback: '',
        },
        '%%positionSumBrutto%%': {
          value: element.isGroup
            ? element.showSubtotal
              ? groupSumWrapper(element.sumBrutto) + ' €'
              : ''
            : numberToString(
                round(
                  element.price * element.amount * (1 + element.taxRate / 100),
                ),
              ),
          fallback: '',
        },
        '%%positionSumNetto%%': {
          value: element.isGroup
            ? element.showSubtotal
              ? groupSumWrapper(element.sumNetto)
              : ''
            : numberToString(round(element.price * element.amount)),
          fallback: '',
        },
        '%%positionSumTax%%': {
          value: element.isGroup
            ? element.showSubtotal
              ? groupSumWrapper(element.sumBrutto - element.sumNetto)
              : ''
            : numberToString(round(element.price * (element.taxRate / 100))),
          fallback: '',
        },
        '%%positionTaxationRate%%': {
          value: element.isGroup ? '' : element.taxRate,
          fallback: '',
        },
        '%%positionDescription%%': {
          value: element.isGroup ? '' : element.description,
          fallback: '',
        },
        '%%positionImage%%': {
          value: element.isGroup
            ? ''
            : element.image
              ? element.image.temporaryUrl
              : '',
          fallback: '',
        },
        // beauftragt in €
        '%%positionOrderSumNetto%%': {
          value: positionOrderSumNetto,
          fallback: '',
        },
        // abrechenbar in €
        '%%positionBillablePrice%%': {
          value: element.isGroup ? '' : abrechenbarPrice,
          fallback: '',
        },

        // abrechenbar in %
        '%%positionBillablePercentage%%': {
          value: PosBillablePercentage,
          fallback: '',
        },
        // bereits verrechnet in €
        '%%positionBilledPrice%%': {
          value: renderedBilledTotal,
          fallback: '',
        },
        '%%positionDepth%%': {
          value: element.depth,
          fallback: 0,
          noHighlight: true,
        },
        // Hoai hoaiValidFrom (Version gemäß HOAI:)
        '%%hoaiValidFrom%%': {
          value: moment(hoaiData?.validFrom).format('DD.MM.YYYY'),
          fallback: '',
        },

        // Hoai Label (Planungsleistung für:)
        '%%hoaiLabel%%': {
          value: hoaiData?.label,
          fallback: '',
        },

        // Hoai hoaiBaseAmount (anrechenbare Baukosten:)
        '%%hoaiBaseAmount%%': {
          value: numberToString(hoaiData?.baseAmount, 2),
          fallback: '',
        },

        // Hoai hoaiNettoHonorarium (Honorar nach Honorartafel zu 35, Abs. 1)
        '%%hoaiNettoHonorarium%%': {
          value: numberToString(hoaiData?.baseHonorarium, 2),
          fallback: '',
        },

        // Hoai hoaiHonorariumZone (Honorarzone-Planungsanforderung)
        '%%hoaiHonorariumZone%%': {
          value: intToRoman(hoaiData?.honorariumZone),
          fallback: '',
        },

        // Hoai hoaiHonorariumZonePercentage (Basishonorarsatz)
        '%%hoaiHonorariumZonePercentage%%': {
          value: numberToString(hoaiData?.honorariumRate, 2),
          fallback: '',
        },

        // Hoai hoaiNextBaseHonorarium (Grundhonorar)
        '%%hoaiNextBaseHonorarium%%': {
          value: numberToString(hoaiData?.nextBaseHonorarium, 2),
          fallback: '',
        },
        // to hide the Additional Services from position list
        '%%positionHoaiType%%': {
          value: element.hoaiType || '',
          fallback: '',
          noHighlight: true,
        },
        '%%positionType%%': {
          value: element.isGroup ? 'group' : element.type || '',
          fallback: '',
          noHighlight: true,
        },
        // beauftragt in %
        // Precision flat: amount=1, price=netto. Calculate % from (price + billedTotal) / baseHonorarium.
        // Normal + user-flat: amount=percent/100, use directly. See billableUnitsNum above.
        '%%positionBillableUnits%%': {
          value: element.isGroup
            ? ''
            : element.hoaiId
              ? numberToString(billableUnitsNum * 100, 2) + ' %'
              : numberToString(billableUnitsNum, 2) + ` ${element.unit || ''}`,
          fallback: '',
        },

        //stand (date) - billed in % (numBilledAmount + numAmount)
        '%%positionBilledUnits%%': {
          value: element.isGroup
            ? ''
            : element.hoaiId
              ? numberToString(billedUnitsNum * 100, 2)
              : numberToString(billedUnitsNum, 2),
          // value: renderedAmount,
          fallback: '',
        },
        // Einzelpreis (Bezugswert) je Zustand — bei Prozentpositionen bleibt die Menge
        // gleich und der Bezugswert unterscheidet sich, daher eigene Variablen.
        '%%positionBilledPricePerUnit%%': {
          value:
            element.isGroup || !billedUnitsDivisor
              ? ''
              : numberToString(numBilledTotal / billedUnitsDivisor, 2),
          fallback: '',
        },
        '%%positionBillablePricePerUnit%%': {
          value:
            element.isGroup || !billableUnitsDivisor
              ? ''
              : numberToString(abrechenbarPriceNum / billableUnitsDivisor, 2),
          fallback: '',
        },
        '%%positionIsPostPosition%%': {
          value: element.isPostPosition ? 'post-position' : '',
          fallback: '',
        },
        '%%positionIsHoai%%': {
          value: element.hoaiId ? 'hoai-position' : '',
        },
        '%%positionHoaiDefaultAmount%%': {
          value: positionHoaiDefaultAmount,
          fallback: '',
        },
        '%%positionHoaiDefaultSumNetto%%': {
          value: positionHoaiDefaultSumNetto,
          fallback: '',
        },
      };

      // remove elements that are overwritten
      const arrOfValuesToRemove = ['%%positionImage%%', '%%positionName%%'];
      const filteredGlobalTemplateVars = globalTemplateVars.filter(
        (x) => !arrOfValuesToRemove.includes(x.value),
      );

      const positionTemplateVars = [
        ...filteredGlobalTemplateVars,
        {
          name: '',
          value: '%%positionIsHoai%%',
          preview: '',
        },
        {
          name: '',
          value: '%%positionPosition%%',
          preview: '',
        },
        {
          name: '',
          value: '%%positionName%%',
          preview: '',
        },
        {
          name: '',
          value: '%%positionAmount%%',
          preview: '',
        },
        {
          name: '',
          value: '%%positionUnit%%',
          preview: '',
        },
        {
          name: '',
          value: '%%positionPrice%%',
          preview: '',
        },
        {
          name: '',
          value: '%%positionSumBrutto%%',
          preview: '',
        },
        {
          name: '',
          value: '%%positionSumNetto%%',
          preview: '',
        },
        {
          name: '',
          value: '%%positionSumTax%%',
          preview: '',
        },

        {
          name: '',
          value: '%%positionTaxationRate%%',
          preview: '',
        },
        {
          name: '',
          value: '%%positionDescription%%',
          preview: '',
        },
        {
          name: '',
          value: '%%positionImage%%',
          preview: '',
          noHighlight: true,
        },
        {
          name: '',
          value: '%%positionImageAreaStart%%',
          preview: '',
          noHighlight: true,
        },
        {
          name: '',
          value: '%%positionImageAreaEnd%%',
          preview: '',
          noHighlight: true,
        },
        {
          name: '',
          value: '%%positionOptionalAdditionStart%%',
          preview: '',
          noHighlight: true,
        },
        {
          name: '',
          value: '%%positionOptionalAdditionEnd%%',
          preview: '',
          noHighlight: true,
        },
        {
          name: '',
          value: '%%positionBillableUnits%%',
          preview: '',
          noHighlight: true,
        },
        {
          name: '',
          value: '%%positionHoaiType%%',
          preview: '',
          noHighlight: true,
        },
        {
          name: '',
          value: '%%positionBilledUnits%%',
          preview: '',
          noHighlight: true,
        },
        {
          name: '',
          value: '%%positionHoaiDefaultAmount%%',
          preview: '',
        },
        {
          name: '',
          value: '%%positionHoaiDefaultSumNetto%%',
          preview: '',
        },
      ];

      // remove position is optional addition
      var templateToRender = template;

      if (element.isGroup) {
        const startKey = '%%positionGroupHiddenStart%%';
        const endKey = '%%positionGroupHiddenEnd%%';
        const replaceKey = '';

        const imageTemplateArea = getTemplateArea(
          templateToRender,
          startKey,
          endKey,
          replaceKey,
        );
        templateToRender = imageTemplateArea.modifiedContent;
      }

      if (!element.isOptional) {
        const startKey = '%%positionOptionalAdditionStart%%';
        const endKey = '%%positionOptionalAdditionEnd%%';
        const replaceKey = '';

        const imageTemplateArea = getTemplateArea(
          templateToRender,
          startKey,
          endKey,
          replaceKey,
        );
        templateToRender = imageTemplateArea.modifiedContent;
      }

      // remove position img if empty
      if (!element.image) {
        const startKey = '%%positionImageAreaStart%%';
        const endKey = '%%positionImageAreaEnd%%';
        const replaceKey = '';

        const imageTemplateArea = getTemplateArea(
          templateToRender,
          startKey,
          endKey,
          replaceKey,
        );
        templateToRender = imageTemplateArea.modifiedContent;
      }

      // remove descripiton segmentation
      const startKey = '%%positionDescriptionFragmentStart%%';
      const endKey = '%%positionDescriptionFragmentEnd%%';
      const replaceKey = '';

      const imageTemplateArea = getTemplateArea(
        templateToRender,
        startKey,
        endKey,
        replaceKey,
      );
      templateToRender = imageTemplateArea.modifiedContent;

      renderedTemplatePositions += TemplateVarSwitcher(
        templateToRender,
        replaceTemplateVars(positionTemplateVars, positionVarsMap),
      );
    });
  }

  return renderedTemplatePositions;
}

function generateRenderedOrderTable(
  template,
  dataCollection,
  globalTemplateVars,
  valueKeyMap,
  hoaiData,
) {
  if (!dataCollection || !template) return;
  const {
    orderPositions,
    orderTitle,
    orderNr,
    orderDate,
    orderPrice,
    showOrderPositions,
  } = dataCollection;

  const positionsStartKey = '%%positionRowStart%%';
  const positionsEndKey = '%%positionRowEnd%%';
  const positionsReplaceKey = '%%orderPositions%%';

  var renderedTemplate = template;
  // replace positions row with placeholder
  const positionsTemplateArea = getTemplateArea(
    renderedTemplate,
    positionsStartKey,
    positionsEndKey,
    positionsReplaceKey,
  );
  renderedTemplate = positionsTemplateArea.modifiedContent;
  const positionsTemplate = positionsTemplateArea.contentBetweenKeys[0];

  //render positions template with real data
  const renderedPositions = showOrderPositions
    ? generateRenderedTemplatePosition(
        positionsTemplate,
        orderPositions,
        globalTemplateVars,
        hoaiData,
      )
    : '';

  // replace temporary placeholder for positions row
  renderedTemplate = renderedTemplate.replace(
    positionsReplaceKey,
    renderedPositions,
  );

  // Recalculate position-dependent variables using orderPositions
  // so that variables inside the order table reflect order data, not invoice data
  const orderValueKeyMap = { ...valueKeyMap };

  if (orderPositions) {
    let orderNettoSum = 0;
    let orderBruttoSum = 0;
    let orderSubtotalNetto = 0;
    orderPositions.forEach((pos) => {
      if (pos.isOptional) return;
      orderNettoSum += round(pos.price * pos.amount);
      orderBruttoSum += round(pos.price * pos.amount * (1 + pos.taxRate / 100));
      orderSubtotalNetto += pos.isPostPosition
        ? 0
        : round(pos.price * pos.amount);
    });

    orderValueKeyMap['%%positionsSumNetto%%'] = {
      value: numberToString(orderNettoSum),
      fallback: '0.00',
      noHighlight: true,
    };
    orderValueKeyMap['%%positionsSumBrutto%%'] = {
      value: numberToString(orderBruttoSum),
      fallback: '0.00',
      noHighlight: true,
    };
    orderValueKeyMap['%%positionsSumTax%%'] = {
      value: numberToString(orderBruttoSum - orderNettoSum),
      fallback: '0.00',
      noHighlight: true,
    };
    orderValueKeyMap['%%positionsSubtotalNetto%%'] = {
      value: numberToString(orderSubtotalNetto),
      fallback: '0.00',
      noHighlight: true,
    };

    // HOAI default phases from order positions
    let totalHoaiDefaultAmount = 0;
    let totalHoaiDefaultSumNetto = 0;
    let hasAnyHoaiDefault = false;
    orderPositions.forEach((pos) => {
      if (pos.type !== 'hoaiPhase' || pos.isGroup || pos.isOptional) return;
      const rawMax =
        pos.hoaiSiemonPosition?.maxPercent ?? pos.hoaiPosition?.maxPercent;
      const defaultPercent = rawMax != null ? parseFloat(rawMax) : 0;
      if (defaultPercent <= 0) return;
      totalHoaiDefaultAmount += defaultPercent;
      totalHoaiDefaultSumNetto += round(
        (defaultPercent / 100) * parseFloat(pos.price || 0),
      );
      hasAnyHoaiDefault = true;
    });

    orderValueKeyMap['%%positionsHoaiDefaultAmount%%'] = {
      value: hasAnyHoaiDefault
        ? `${numberToString(totalHoaiDefaultAmount, 2)} %`
        : '',
      fallback: '',
      noHighlight: true,
    };
    orderValueKeyMap['%%hoaiDefaultPhasesNetto%%'] = {
      value: hasAnyHoaiDefault
        ? numberToString(totalHoaiDefaultSumNetto, 2)
        : '',
      fallback: '',
      noHighlight: true,
    };

    // Recalculate all HOAI variables (phases, surcharge, etc.) from order positions
    const orderHoaiValueKeyMap = generateHoaiValueKeyMap(
      orderPositions,
      hoaiData,
    );
    Object.assign(orderValueKeyMap, orderHoaiValueKeyMap);
  }

  return TemplateVarSwitcher(
    renderedTemplate,
    replaceTemplateVars(globalTemplateVars, orderValueKeyMap),
  );
}

function replaceTemplateVars(templateVars, varsMap, positions = []) {
  const newTemplateVars = [];

  function buildPreview(element, mapEntry) {
    if (element.noHighlight || mapEntry.noHighlight) {
      return mapEntry.value;
    }
    if (mapEntry.value) {
      return `<span class="templateVar" style=" display: inline; font-family: inherit;
    font-size: inherit;
    line-height: inherit; text-align: inherit;">${mapEntry.value}</span>`;
    }
    return `<span class="templateVarFallback" style=" display: inline; font-family: inherit;
        font-size: inherit;
        line-height: inherit;  text-align: inherit;">${mapEntry.fallback}</span>`;
  }

  // normal template Vars
  templateVars.forEach((element) => {
    if (varsMap[element.value]) {
      var updatedVar = element;
      updatedVar.preview = buildPreview(element, varsMap[element.value]);
      newTemplateVars.push(updatedVar);
    } else {
      newTemplateVars.push(element);
    }
  });

  return newTemplateVars;
}

function convertMetaFieldsToValueKeyMap(config, data, metaFields) {
  if (!metaFields || !metaFields.length) return {};

  var metaFieldsKeyValueMap = {};

  metaFields.forEach((field) => {
    const { key, preview, name, value, type } = field;
    if (type === 'money') {
      metaFieldsKeyValueMap[value] = {
        value: data.metaFields
          ? numberToString(stringToNumber(data.metaFields[key])) || ''
          : numberToString(0),
        fallback: '',
        noHighlight: true,
      };
    } else if (type === 'date') {
      metaFieldsKeyValueMap[value] = {
        value: data.metaFields
          ? moment(data.metaFields[key]).format(
              config?.general?.dateOutputFormat || 'DD.MM.YYYY', //Fallback value
            ) || ''
          : '',
        fallback: '',
        noHighlight: true,
      };
    } else {
      metaFieldsKeyValueMap[value] = {
        value: data.metaFields ? data.metaFields[key] || '' : '',
        fallback: '',
        noHighlight: true,
      };
    }
  });

  return metaFieldsKeyValueMap;
}

export function convertMetaFieldsToTemplateVars(
  metaFields,
  usePreview = false,
) {
  if (!metaFields || !metaFields.length) return [];

  var metaFieldsTemplateVars = [];
  metaFields.forEach((field) => {
    const { name, preview, value } = field;
    metaFieldsTemplateVars.push({
      name,
      value,
      preview: usePreview ? preview || name : '',
    });
  });

  return metaFieldsTemplateVars;
}

function generateHoaiValueKeyMap(positions, hoaiData) {
  if (!positions || !positions.length) return {};
  var hoaiVarValues = {
    hoaiPhasesNetto: { billed: 0, current: 0, billable: 0, ordered: 0 },
    hoaiPhasesAmount: { billed: 0, current: 0, billable: 0, ordered: 0 },

    hoaiSurchargeAmount: { billed: 0, current: 0, billable: 0, ordered: 0 },
    hoaiSurchargeNetto: { billed: 0, current: 0, billable: 0, ordered: 0 },
    hoaiSurchargeAmount: { billed: 0, current: 0, billable: 0, ordered: 0 },
    hoaiSurchargeNettoSubtotal: {
      billed: 0,
      current: 0,
      billable: 0,
      ordered: 0,
    },

    hoaiTimeFeeNetto: { billed: 0, current: 0, billable: 0, ordered: 0 },
    hoaiTimeFeeNettoSubtotal: {
      billed: 0,
      current: 0,
      billable: 0,
      ordered: 0,
    },

    hoaiSpecialServicesNetto: {
      billed: 0,
      current: 0,
      billable: 0,
      ordered: 0,
    },
    hoaiSpecialServicesNettoSubtotal: {
      billed: 0,
      current: 0,
      billable: 0,
      ordered: 0,
    },

    hoaiAdditionalServicesNetto: {
      billed: 0,
      current: 0,
      billable: 0,
      ordered: 0,
    },
    hoaiAdditionalServicesNettoSubtotal: {
      billed: 0,

      current: 0,
      billable: 0,
      ordered: 0,
    },

    hoaiIncidentalCostsAmount: {
      billed: 0,
      current: 0,
      billable: 0,
      ordered: 0,
    },
    hoaiIncidentalCostsNetto: {
      billed: 0,
      current: 0,
      billable: 0,
      ordered: 0,
    },
    hoaiIncidentalCostsNettoSubtotal: {
      billed: 0,
      current: 0,
      billable: 0,
      ordered: 0,
    },
  };

  var newMap = {};

  // honorar rate label
  const rate = parseFloat(
    hoaiData?.honorariumRate || hoaiData?.honorarium_rate || 0,
  );
  newMap['%%hoaiHonorariumZoneRate%%'] = {
    value: getHonorariumRateLabel(rate) || '',
    fallback: '',
  };

  // -----calculate phases-----
  const hoaiPhasePositions = positions?.filter(
    (obj) => obj.hoaiType === 'phase' || obj.hoaiType === 'siemonPhase',
  );
  const baseHon = hoaiData?.baseHonorarium;
  hoaiPhasePositions.forEach((element) => {
    const elAmount = parseFloat(element.amount);
    const elPrice = parseFloat(element.price);
    const elBilledAmount = parseFloat(element.billedAmount || 0);
    const elBilledTotal = parseFloat(element.billedTotal || 0);
    const elOrderedAmount = parseFloat(element.orderedAmount || 0);
    const elOrderedPrice = parseFloat(
      element.orderedPrice || element.price || 0,
    );
    const isPrecisionFlat =
      elAmount === 1 &&
      !element.isFlat &&
      element.hoaiPosition?.type !== 'flat';

    // current
    hoaiVarValues.hoaiPhasesAmount.current += round(
      isPrecisionFlat && baseHon ? (elPrice / baseHon) * 100 : elAmount * 100,
    );
    hoaiVarValues.hoaiPhasesNetto.current += round(elPrice * elAmount);

    // billed
    hoaiVarValues.hoaiPhasesAmount.billed += round(
      isPrecisionFlat && baseHon
        ? (elBilledTotal / baseHon) * 100
        : elBilledAmount * 100,
    );
    hoaiVarValues.hoaiPhasesNetto.billed += round(elBilledTotal);

    // ordered
    hoaiVarValues.hoaiPhasesAmount.ordered += round(
      isPrecisionFlat && baseHon
        ? ((elOrderedPrice * elOrderedAmount) / baseHon) * 100
        : elOrderedAmount * 100,
    );
    hoaiVarValues.hoaiPhasesNetto.ordered += round(
      elOrderedPrice * elOrderedAmount,
    );
  });
  // billable
  hoaiVarValues.hoaiPhasesAmount.billable = round(
    hoaiVarValues.hoaiPhasesAmount.current +
      hoaiVarValues.hoaiPhasesAmount.billed,
  );
  hoaiVarValues.hoaiPhasesNetto.billable = round(
    hoaiVarValues.hoaiPhasesNetto.current +
      hoaiVarValues.hoaiPhasesNetto.billed,
  );

  // -----calculate surcharge-----
  const hoaiSurchargePositions = positions?.filter(
    (obj) => obj.hoaiType === 'surcharge',
  );
  newMap['%%hasHoaiSurchargeClassName%%'] = {
    value: hoaiSurchargePositions.length > 0 ? 'has-hoai-surcharge' : '',
    fallback: '',
    noHighlight: true,
  };
  newMap['%%hoaiSurchargeDescription%%'] = {
    value: hoaiSurchargePositions
      .map((pos) => pos.description || '')
      .filter(Boolean)
      .join(', '),
    fallback: '',
  };
  hoaiSurchargePositions.forEach((element) => {
    // current
    hoaiVarValues.hoaiSurchargeAmount.current += round(
      parseFloat(element.amount),
    );
    hoaiVarValues.hoaiSurchargeNetto.current += round(
      parseFloat(element.amount) * parseFloat(element.price),
    );

    // billed
    hoaiVarValues.hoaiSurchargeAmount.billed += round(
      parseFloat(element.billedAmount || 0),
    );
    hoaiVarValues.hoaiSurchargeNetto.billed += round(
      parseFloat(element.billedTotal || 0),
    );

    // ordered
    hoaiVarValues.hoaiSurchargeAmount.ordered += round(
      parseFloat(element.orderedAmount || 0),
    );
    hoaiVarValues.hoaiSurchargeNetto.ordered += round(
      parseFloat(element.orderedPrice || 0) *
        parseFloat(element.orderedAmount || 0),
    );
  });
  // billable
  hoaiVarValues.hoaiSurchargeAmount.billable = round(
    hoaiVarValues.hoaiSurchargeAmount.current +
      hoaiVarValues.hoaiSurchargeAmount.billed,
  );
  hoaiVarValues.hoaiSurchargeNetto.billable = round(
    hoaiVarValues.hoaiSurchargeNetto.current +
      hoaiVarValues.hoaiSurchargeNetto.billed,
  );

  // surcharge subtotal  [hoaiPhases + hoaiSurcharge]
  hoaiVarValues.hoaiSurchargeNettoSubtotal.ordered = round(
    hoaiVarValues.hoaiPhasesNetto.ordered +
      hoaiVarValues.hoaiSurchargeNetto.ordered,
  );
  hoaiVarValues.hoaiSurchargeNettoSubtotal.current = round(
    hoaiVarValues.hoaiPhasesNetto.current +
      hoaiVarValues.hoaiSurchargeNetto.current,
  );
  hoaiVarValues.hoaiSurchargeNettoSubtotal.billed = round(
    hoaiVarValues.hoaiPhasesNetto.billed +
      hoaiVarValues.hoaiSurchargeNetto.billed,
  );
  hoaiVarValues.hoaiSurchargeNettoSubtotal.billable = round(
    hoaiVarValues.hoaiPhasesNetto.billable +
      hoaiVarValues.hoaiSurchargeNetto.billable,
  );

  // -----calculate time fee-----
  const hoaiTimeFeePositions = positions?.filter(
    (obj) => obj.hoaiType === 'timeFee',
  );
  newMap['%%hasHoaiTimeFeeClassName%%'] = {
    value: hoaiTimeFeePositions.length > 0 ? 'has-hoai-time-fee' : '',
    fallback: '',
    noHighlight: true,
  };
  newMap['%%hoaiTimeFeeDescription%%'] = {
    value: hoaiTimeFeePositions
      .map((pos) => pos.description || '')
      .filter(Boolean)
      .join(', '),
    fallback: '',
  };
  hoaiTimeFeePositions.forEach((element) => {
    // current
    hoaiVarValues.hoaiTimeFeeNetto.current += round(
      parseFloat(element.price) * parseFloat(element.amount),
    );

    // billed
    hoaiVarValues.hoaiTimeFeeNetto.billed += round(
      parseFloat(element.billedTotal || 0),
    );

    // ordered
    hoaiVarValues.hoaiTimeFeeNetto.ordered += round(
      parseFloat(element.orderedPrice || 0) *
        parseFloat(element.orderedAmount || 0),
    );
  });
  hoaiVarValues.hoaiTimeFeeNetto.billable = round(
    hoaiVarValues.hoaiTimeFeeNetto.current +
      hoaiVarValues.hoaiTimeFeeNetto.billed,
  );

  // time fee subtotal [hoaiSurchargeSubtotal + hoaiTimeFee]
  hoaiVarValues.hoaiTimeFeeNettoSubtotal.ordered = round(
    hoaiVarValues.hoaiSurchargeNettoSubtotal.ordered +
      hoaiVarValues.hoaiTimeFeeNetto.ordered,
  );
  hoaiVarValues.hoaiTimeFeeNettoSubtotal.current = round(
    hoaiVarValues.hoaiSurchargeNettoSubtotal.current +
      hoaiVarValues.hoaiTimeFeeNetto.current,
  );
  hoaiVarValues.hoaiTimeFeeNettoSubtotal.billed = round(
    hoaiVarValues.hoaiSurchargeNettoSubtotal.billed +
      hoaiVarValues.hoaiTimeFeeNetto.billed,
  );
  hoaiVarValues.hoaiTimeFeeNettoSubtotal.billable = round(
    hoaiVarValues.hoaiSurchargeNettoSubtotal.billable +
      hoaiVarValues.hoaiTimeFeeNetto.billable,
  );

  // -----calculate special services-----
  const hoaiSpecialServicesPositions = positions?.filter(
    (obj) => obj.hoaiType === 'specialServices',
  );
  newMap['%%hasHoaiSpecialServicesClassName%%'] = {
    value:
      hoaiSpecialServicesPositions.length > 0
        ? 'has-hoai-special-services'
        : '',
    fallback: '',
    noHighlight: true,
  };
  newMap['%%hoaiSpecialServicesDescription%%'] = {
    value: hoaiSpecialServicesPositions
      .map((pos) => pos.description || '')
      .filter(Boolean)
      .join(', '),
    fallback: '',
  };
  hoaiSpecialServicesPositions.forEach((element) => {
    // current
    hoaiVarValues.hoaiSpecialServicesNetto.current += round(
      parseFloat(element.price) * parseFloat(element.amount),
    );

    // billed
    hoaiVarValues.hoaiSpecialServicesNetto.billed += round(
      parseFloat(element.billedTotal || 0),
    );

    // ordered
    hoaiVarValues.hoaiSpecialServicesNetto.ordered += round(
      parseFloat(element.orderedPrice || 0) *
        parseFloat(element.orderedAmount || 0),
    );
  });
  hoaiVarValues.hoaiSpecialServicesNetto.billable = round(
    hoaiVarValues.hoaiSpecialServicesNetto.current +
      hoaiVarValues.hoaiSpecialServicesNetto.billed,
  );

  // special services subtotal [hoaiTimeFeeSubtotal + hoaiSpecialServices]
  hoaiVarValues.hoaiSpecialServicesNettoSubtotal.ordered = round(
    hoaiVarValues.hoaiTimeFeeNettoSubtotal.ordered +
      hoaiVarValues.hoaiSpecialServicesNetto.ordered,
  );
  hoaiVarValues.hoaiSpecialServicesNettoSubtotal.current = round(
    hoaiVarValues.hoaiTimeFeeNettoSubtotal.current +
      hoaiVarValues.hoaiSpecialServicesNetto.current,
  );
  hoaiVarValues.hoaiSpecialServicesNettoSubtotal.billed = round(
    hoaiVarValues.hoaiTimeFeeNettoSubtotal.billed +
      hoaiVarValues.hoaiSpecialServicesNetto.billed,
  );
  hoaiVarValues.hoaiSpecialServicesNettoSubtotal.billable = round(
    hoaiVarValues.hoaiTimeFeeNettoSubtotal.billable +
      hoaiVarValues.hoaiSpecialServicesNetto.billable,
  );

  // -----calculate additional services-----
  const hoaiAdditionalServicesPositions = positions?.filter(
    (obj) => obj.hoaiType === 'additionalServices',
  );
  newMap['%%hasHoaiAdditionalServicesClassName%%'] = {
    value:
      hoaiAdditionalServicesPositions.length > 0
        ? 'has-hoai-additional-services'
        : '',
    fallback: '',
    noHighlight: true,
  };
  newMap['%%hoaiAdditionalServicesDescription%%'] = {
    value: hoaiAdditionalServicesPositions
      .map((pos) => pos.description || '')
      .filter(Boolean)
      .join(', '),
    fallback: '',
  };
  hoaiAdditionalServicesPositions.forEach((element) => {
    // current
    hoaiVarValues.hoaiAdditionalServicesNetto.current += round(
      parseFloat(element.price) * parseFloat(element.amount),
    );

    // billed
    hoaiVarValues.hoaiAdditionalServicesNetto.billed += round(
      parseFloat(element.billedTotal || 0),
    );

    // ordered
    hoaiVarValues.hoaiAdditionalServicesNetto.ordered += round(
      parseFloat(element.orderedPrice || 0) *
        parseFloat(element.orderedAmount || 0),
    );
  });
  hoaiVarValues.hoaiAdditionalServicesNetto.billable = round(
    hoaiVarValues.hoaiAdditionalServicesNetto.current +
      hoaiVarValues.hoaiAdditionalServicesNetto.billed,
  );

  // additional services subtotal [hoaiSpecialServicesSubtotal + hoaiAdditionalServices]
  hoaiVarValues.hoaiAdditionalServicesNettoSubtotal.ordered = round(
    hoaiVarValues.hoaiSpecialServicesNettoSubtotal.ordered +
      hoaiVarValues.hoaiAdditionalServicesNetto.ordered,
  );
  hoaiVarValues.hoaiAdditionalServicesNettoSubtotal.current = round(
    hoaiVarValues.hoaiSpecialServicesNettoSubtotal.current +
      hoaiVarValues.hoaiAdditionalServicesNetto.current,
  );
  hoaiVarValues.hoaiAdditionalServicesNettoSubtotal.billed = round(
    hoaiVarValues.hoaiSpecialServicesNettoSubtotal.billed +
      hoaiVarValues.hoaiAdditionalServicesNetto.billed,
  );
  hoaiVarValues.hoaiAdditionalServicesNettoSubtotal.billable = round(
    hoaiVarValues.hoaiSpecialServicesNettoSubtotal.billable +
      hoaiVarValues.hoaiAdditionalServicesNetto.billable,
  );

  // -----calculate incidental costs-----
  const hoaiIncidentalCostsPositions = positions?.filter(
    (obj) => obj.hoaiType === 'incidentalCosts',
  );

  newMap['%%hasHoaiIncidentalCostsClassName%%'] = {
    value:
      hoaiIncidentalCostsPositions.length > 0
        ? 'has-hoai-incidental-costs'
        : '',
    fallback: '',
    noHighlight: true,
  };
  newMap['%%hoaiIncidentalCostsDescription%%'] = {
    value: hoaiIncidentalCostsPositions
      .map((pos) => pos.description || '')
      .filter(Boolean)
      .join(', '),
    fallback: '',
  };

  hoaiIncidentalCostsPositions.forEach((element) => {
    // current
    hoaiVarValues.hoaiIncidentalCostsAmount.current += round(
      parseFloat(element.amount),
    );
    hoaiVarValues.hoaiIncidentalCostsNetto.current += round(
      parseFloat(element.price) * parseFloat(element.amount),
    );

    // billed
    hoaiVarValues.hoaiIncidentalCostsAmount.billed += round(
      parseFloat(element.billedAmount || 0),
    );
    hoaiVarValues.hoaiIncidentalCostsNetto.billed += round(
      parseFloat(element.billedTotal || 0),
    );

    // ordered
    hoaiVarValues.hoaiIncidentalCostsAmount.ordered += round(
      parseFloat(element.orderedAmount || 0),
    );
    hoaiVarValues.hoaiIncidentalCostsNetto.ordered += round(
      parseFloat(element.orderedPrice || 0) *
        parseFloat(element.orderedAmount || 0),
    );
  });
  hoaiVarValues.hoaiIncidentalCostsNetto.billable = round(
    hoaiVarValues.hoaiIncidentalCostsNetto.current +
      hoaiVarValues.hoaiIncidentalCostsNetto.billed,
  );
  hoaiVarValues.hoaiIncidentalCostsAmount.billable = round(
    hoaiVarValues.hoaiIncidentalCostsAmount.current +
      hoaiVarValues.hoaiIncidentalCostsAmount.billed,
  );

  // incidental costs subtotal [hoaiAdditionalServicesSubtotal + hoaiIncidentalCosts]
  hoaiVarValues.hoaiIncidentalCostsNettoSubtotal.ordered = round(
    hoaiVarValues.hoaiAdditionalServicesNettoSubtotal.ordered +
      hoaiVarValues.hoaiIncidentalCostsNetto.ordered,
  );
  hoaiVarValues.hoaiIncidentalCostsNettoSubtotal.current = round(
    hoaiVarValues.hoaiAdditionalServicesNettoSubtotal.current +
      hoaiVarValues.hoaiIncidentalCostsNetto.current,
  );
  hoaiVarValues.hoaiIncidentalCostsNettoSubtotal.billed = round(
    hoaiVarValues.hoaiAdditionalServicesNettoSubtotal.billed +
      hoaiVarValues.hoaiIncidentalCostsNetto.billed,
  );
  hoaiVarValues.hoaiIncidentalCostsNettoSubtotal.billable = round(
    hoaiVarValues.hoaiAdditionalServicesNettoSubtotal.billable +
      hoaiVarValues.hoaiIncidentalCostsNetto.billable,
  );
  // genereate the final map
  const keys = Object.keys(hoaiVarValues);

  const percentKeys = ['hoaiSurchargeAmount', 'hoaiIncidentalCostsAmount'];

  keys.forEach((key) => {
    const isPercent = percentKeys.includes(key);
    const dp = isPercent ? null : 2;
    const suffix = isPercent ? ' %' : '';
    const mul = isPercent ? 100 : 1;
    newMap[`%%${key}Ordered%%`] = {
      value: numberToString(hoaiVarValues[key].ordered * mul, dp) + suffix,
      fallback: '',
    };
    newMap[`%%${key}%%`] = {
      value: numberToString(hoaiVarValues[key].current * mul, dp) + suffix,
      fallback: '',
    };
    newMap[`%%${key}Billed%%`] = {
      value: numberToString(hoaiVarValues[key].billed * mul, dp) + suffix,
      fallback: '',
    };
    newMap[`%%${key}Billable%%`] = {
      value: numberToString(hoaiVarValues[key].billable * mul, dp) + suffix,
      fallback: '',
    };
  });

  return newMap;
}

// =============================================================================
// InlineAttachments Helper Functions
// =============================================================================

/**
 * Formats seconds to HH:mm string
 */
function formatDuration(seconds) {
  // Handle negative values robustly
  const absSeconds = Math.abs(seconds || 0);
  const hours = Math.floor(absSeconds / 3600);
  const minutes = Math.floor((absSeconds % 3600) / 60);
  const sign = seconds < 0 ? '-' : '';
  return `${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

/**
 * Generates timesheet content for preview
 * Mirrors backend: CreatePdf.php:replaceInlineTimeTable()
 */
function generateTimesheetContent(
  template,
  timeTrackings,
  billingConfig = {},
  adjustments = {},
) {
  if (!template?.content || !timeTrackings?.length) return '';

  const rowStartKey = '%%timeTrackingRowStart%%';
  const rowEndKey = '%%timeTrackingRowEnd%%';

  // Extract row template
  const startIdx = template.content.indexOf(rowStartKey);
  const endIdx = template.content.indexOf(rowEndKey);
  if (startIdx === -1 || endIdx === -1) return template.content;

  const rowTemplate = template.content.substring(
    startIdx + rowStartKey.length,
    endIdx,
  );

  // Build hourly rate map from billingConfig.wagePresets
  const hourlyRateMap = {};
  const wagePresets =
    billingConfig?.wagePresets || billingConfig?.wage_presets || [];
  wagePresets.forEach((preset) => {
    const wage = preset.wage || 0;
    const ttIds = preset.timeTrackingIds || preset.time_tracking_ids || [];
    ttIds.forEach((ttId) => {
      hourlyRateMap[ttId] = wage;
      hourlyRateMap[String(ttId)] = wage;
    });
  });

  // Generate rows
  let tableContent = '';
  let totalSeconds = 0;
  let totalAmount = 0;

  // Sort time trackings by start date ascending (use adjusted time if available)
  // Handle both camelCase (from API) and snake_case (from frontend) formats
  const sortedTimeTrackings = [...timeTrackings].sort((a, b) => {
    const adjA = adjustments[a.id] || adjustments[String(a.id)] || {};
    const adjB = adjustments[b.id] || adjustments[String(b.id)] || {};
    const startA = moment(
      adjA.time_start || adjA.timeStart || a.timeStart || a.time_start,
    );
    const startB = moment(
      adjB.time_start || adjB.timeStart || b.timeStart || b.time_start,
    );
    return startA.valueOf() - startB.valueOf();
  });

  sortedTimeTrackings.forEach((tt) => {
    // Apply adjustments
    const adj = adjustments[tt.id] || adjustments[String(tt.id)] || {};

    // Skip excluded entries
    if (adj.exclude_from_report || adj.excludeFromReport) return;

    // Times (adjusted or original)
    // Handle both camelCase (from API) and snake_case (from frontend) formats
    const adjTimeStart = adj.time_start || adj.timeStart;
    const adjTimeEnd = adj.time_end || adj.timeEnd;

    // FIX: Parse original times first, then apply adjustments
    // This ensures consistent timezone handling even if only one time was adjusted
    const originalStart = moment(tt.timeStart || tt.time_start);
    const originalEnd = moment(tt.timeEnd || tt.time_end);

    // If adjustment exists for a time, use it; otherwise use original
    // Parse adjusted times with moment to ensure consistent handling
    const timeStart = adjTimeStart ? moment(adjTimeStart) : originalStart;
    const timeEnd = adjTimeEnd ? moment(adjTimeEnd) : originalEnd;

    // FIX: If we have mixed sources (one adjusted, one original), ensure both are in local time
    // by formatting and re-parsing to strip timezone info
    const hasAdjustment = adjTimeStart || adjTimeEnd;
    const hasMixedSources =
      (adjTimeStart && !adjTimeEnd) || (!adjTimeStart && adjTimeEnd);
    let finalStart = timeStart;
    let finalEnd = timeEnd;

    if (hasAdjustment && hasMixedSources) {
      // Normalize both to local time format to prevent timezone mismatch
      finalStart = moment(timeStart.format('YYYY-MM-DD HH:mm:ss'));
      finalEnd = moment(timeEnd.format('YYYY-MM-DD HH:mm:ss'));
    }

    const comment = adj.comment ?? tt.comment ?? '';

    // Calculate duration using normalized times
    const durationSeconds = finalEnd.diff(finalStart, 'seconds');
    totalSeconds += durationSeconds;

    // Hourly rate and amount
    const hourlyRate =
      hourlyRateMap[tt.id] || hourlyRateMap[String(tt.id)] || 0;
    const durationHours = durationSeconds / 3600;
    const entryAmount = hourlyRate * durationHours;
    totalAmount += entryAmount;

    // Get employee data (API returns 'employee' not 'user')
    const employee = tt.employee || tt.user;

    // Row variables (use German locale for day names)
    // Use finalStart/finalEnd for consistent timezone handling
    const rowVars = {
      '%%timeTrackingDate%%': finalStart.format('DD.MM.YY'),
      '%%timeTrackingDayName%%': finalStart.locale('de').format('dd'),
      '%%timeTrackingDayStart%%': finalStart.format('HH:mm'),
      '%%timeTrackingDayEnd%%': finalEnd.format('HH:mm'),
      '%%timeTrackingBreakDuration%%': '',
      '%%timeTrackingDayDurationSum%%': formatDuration(durationSeconds),
      '%%timeTrackingCorrectionReason%%': '',
      '%%timeTrackingCorrectionComment%%': '',
      '%%timeTrackingDayTypeKey%%': '',
      '%%timeTrackingDayTypeExplanation%%': '',
      '%%timeTrackingUserSalutation%%': employee?.salutation || '',
      '%%timeTrackingUserFirstName%%':
        employee?.firstName || employee?.first_name || '',
      '%%timeTrackingUserLastName%%':
        employee?.lastName || employee?.last_name || '',
      '%%timeTrackingProjectNr%%': tt.project?.nr || '',
      '%%timeTrackingProjectName%%': tt.project?.name || '',
      '%%timeTrackingWorkPackageName%%':
        tt.workPackage?.title || tt.workPackage?.name || '',
      '%%timeTrackingComment%%': comment,
      '%%timeTrackingHourlyRate%%': numberToString(hourlyRate, 2),
      '%%timeTrackingEntryDuration%%': formatDuration(durationSeconds),
      '%%timeTrackingEntryHoursDecimal%%': numberToString(durationHours, 2),
      '%%timeTrackingEntryAmount%%': numberToString(entryAmount, 2),
    };

    // Replace variables in row
    let row = rowTemplate;
    Object.entries(rowVars).forEach(([key, value]) => {
      row = row.replaceAll(key, value || '');
    });

    tableContent += row;
  });

  // Summary variables
  const totalHours = totalSeconds / 3600;
  const firstTt = timeTrackings[0];
  const firstDate = moment(firstTt?.timeStart || firstTt?.time_start);

  const summaryVars = {
    '%%timeTrackingTotalSum%%': formatDuration(totalSeconds),
    '%%timeTrackingTotalHoursDecimal%%': numberToString(totalHours, 2),
    '%%timeTrackingTotalAmount%%': numberToString(totalAmount, 2),
    '%%timeTrackingMonth%%': firstDate.isValid() ? firstDate.format('MM') : '',
    '%%timeTrackingYear%%': firstDate.isValid() ? firstDate.format('YYYY') : '',
  };

  // Build content
  let content =
    template.content.substring(0, startIdx) +
    tableContent +
    template.content.substring(endIdx + rowEndKey.length);

  // Replace summary variables
  Object.entries(summaryVars).forEach(([key, value]) => {
    content = content.replaceAll(key, value || '');
  });

  return content;
}

/**
 * Flattens nested DIN data structure
 */
function buildDinLookup(dinBaseData) {
  const lookup = {};
  const stack = [...(dinBaseData || [])];
  while (stack.length) {
    const current = stack.pop();
    if (current?.number) {
      // older DIN editions carried "370 " and "750 " with a trailing space
      lookup[String(current.number).trim()] = {
        label: current.label || '',
        description: current.description || '',
      };
    }
    if (current?.phases?.length) {
      stack.push(...current.phases);
    }
  }
  return lookup;
}

function flattenDinData(dinData, dinYear) {
  const result = [];
  const stack = Array.isArray(dinData) ? [...dinData] : [dinData];

  const dinBase = String(dinYear) === '2008' ? din276_08 : din276_18;
  const lookup = buildDinLookup(dinBase.data);

  while (stack.length) {
    const current = stack.pop();
    if (current?.number && current?.costs) {
      const number = String(current.number).trim();
      const base = lookup[number] || {};
      result.push({
        ...current,
        number,
        label: current.label || base.label || '',
        description: current.description || base.description || '',
      });
    }
    if (current?.phases?.length) {
      stack.push(...current.phases);
    }
  }

  return result.sort((a, b) => (a.number || '').localeCompare(b.number || ''));
}

/**
 * Converts integer to Roman numeral
 */
function intToRoman(num) {
  const romanNumerals = ['', 'I', 'II', 'III', 'IV', 'V'];
  return romanNumerals[num] || String(num);
}

/**
 * Gets the honorarium rate label
 */
function getHonorariumRateLabel(rate) {
  const r = parseFloat(rate || 0);
  if (r === 0) return 'Basishonorarsatz';
  if (r === 25) return 'Viertelsatz';
  if (r === 50) return 'Mittelsatz';
  if (r === 75) return 'Dreiviertelsatz';
  if (r === 100) return 'Oberer Honorarsatz';
  return `Eigener Honorarsatz`;
}

/**
 * Builds HOAI variables map
 * Mirrors backend: TemplateVariables.php:getHoaiVars()
 */
function buildHoaiVars(hoai) {
  if (!hoai) return {};

  // Interpolation calculations (analog backend)
  const baseAmount = parseFloat(hoai.baseAmount || hoai.base_amount || 0);
  const prevTable = parseFloat(
    hoai.prevTableHonorarium || hoai.prev_table_honorarium || 0,
  );
  const nextTable = parseFloat(
    hoai.nextTableHonorarium || hoai.next_table_honorarium || 0,
  );
  const prevBase = parseFloat(
    hoai.previousBaseHonorarium || hoai.previous_base_honorarium || 0,
  );
  const prevUpper = parseFloat(
    hoai.previousUpperHonorarium || hoai.previous_upper_honorarium || 0,
  );
  const nextBase = parseFloat(
    hoai.nextBaseHonorarium || hoai.next_base_honorarium || 0,
  );
  const nextUpper = parseFloat(
    hoai.nextUpperHonorarium || hoai.next_upper_honorarium || 0,
  );
  const rate = parseFloat(hoai.honorariumRate || hoai.honorarium_rate || 0);

  const tableDiff = nextTable - prevTable || 1;
  const interpolatedBase =
    prevBase + ((baseAmount - prevTable) * (nextBase - prevBase)) / tableDiff;
  const interpolatedUpper =
    prevUpper +
    ((baseAmount - prevTable) * (nextUpper - prevUpper)) / tableDiff;
  const interpolatedPrev = prevBase + (rate / 100) * (prevUpper - prevBase);
  const interpolatedNext = nextBase + (rate / 100) * (nextUpper - nextBase);

  return {
    '%%hoaiValidFrom%%': hoai.validFrom
      ? moment(hoai.validFrom).format('DD.MM.YYYY')
      : '',
    '%%hoaiYearVersion%%': hoai.validFrom
      ? moment(hoai.validFrom).format('YYYY')
      : '',
    '%%hoaiLabel%%': hoai.label || '',
    '%%hoaiBaseAmount%%': numberToString(baseAmount, 2),
    '%%hoaiBaseAmountUnit%%':
      hoai.baseAmountUnit === 'euro'
        ? 'Euro'
        : hoai.baseAmountUnit === 'ha'
          ? 'Hektar'
          : hoai.baseAmountUnit || '',
    '%%hoaiHonorariumZone%%': intToRoman(
      hoai.honorariumZone || hoai.honorarium_zone,
    ),
    '%%hoaiHonorariumZoneRate%%': getHonorariumRateLabel(rate),
    '%%hoaiHonorariumZonePercentage%%': numberToString(rate, 2),
    '%%hoaiHonorariumZoneFactor%%': numberToString(rate / 100, 4),
    '%%hoaiTableColumnHeader%%':
      hoai.baseAmountUnit === 'euro'
        ? 'Anrechenbare Kosten in Euro'
        : hoai.baseAmountUnit === 'ha'
          ? 'Fläche in Hektar'
          : 'Verrechnungseinheiten',
    '%%hoaiPrevTableHonorarium%%': numberToString(prevTable, 2),
    '%%hoaiNextTableHonorarium%%': numberToString(nextTable, 2),
    '%%hoaiPreviousBaseHonorarium%%': numberToString(prevBase, 2),
    '%%hoaiPreviousUpperHonorarium%%': numberToString(prevUpper, 2),
    '%%hoaiNextBaseHonorarium%%': numberToString(nextBase, 2),
    '%%hoaiNextUpperHonorarium%%': numberToString(nextUpper, 2),
    '%%hoaiNettoHonorarium%%': numberToString(
      hoai.baseHonorarium || hoai.base_honorarium || 0,
      2,
    ),
    '%%hoaiInterpolatedBaseHonorarium%%': numberToString(interpolatedBase, 2),
    '%%hoaiInterpolatedUpperHonorarium%%': numberToString(interpolatedUpper, 2),
    '%%hoaiInterpolatedPreviousTableHonorarium%%': numberToString(
      interpolatedPrev,
      2,
    ),
    '%%hoaiInterpolatedNextTableHonorarium%%': numberToString(
      interpolatedNext,
      2,
    ),
    '%%hoaiCreatedAtYear%%': hoai.createdAt
      ? moment(hoai.createdAt).format('YYYY')
      : '',
    '%%hoaiCreatedAtMonth%%': hoai.createdAt
      ? moment(hoai.createdAt).format('MM')
      : '',
    '%%hoaiCreatedAtDay%%': hoai.createdAt
      ? moment(hoai.createdAt).format('DD')
      : '',
    '%%dinTitle%%': hoai.din?.title || '',
    '%%dinYear%%': hoai.din?.year || '',
    '%%dinCreatedAtMonth%%': hoai.din?.createdAt
      ? moment(hoai.din.createdAt).format('MM')
      : '',
    '%%dinCreatedAtYear%%': hoai.din?.createdAt
      ? moment(hoai.din.createdAt).format('YYYY')
      : '',
    '%%dinCreatedAtDay%%': hoai.din?.createdAt
      ? moment(hoai.din.createdAt).format('DD')
      : '',
    '%%dinTotalCost%%': numberToString(
      hoai.din?.totalCost || hoai.din?.total_cost || 0,
      2,
    ),
  };
}

/**
 * Replaces DIN positions in template
 * Mirrors backend: CreatePdf.php:replaceDinPositions()
 */
function replaceDinPositions(content, dinData, dinYear) {
  const rowStartKey = '%%dinPositionRowStart%%';
  const rowEndKey = '%%dinPositionRowEnd%%';

  const startIdx = content.indexOf(rowStartKey);
  const endIdx = content.indexOf(rowEndKey);
  if (startIdx === -1 || endIdx === -1) return content;

  const rowTemplate = content.substring(startIdx + rowStartKey.length, endIdx);

  if (!dinData) {
    return (
      content.substring(0, startIdx) +
      content.substring(endIdx + rowEndKey.length)
    );
  }

  // Flatten DIN data
  const dinPositions = flattenDinData(dinData, dinYear);

  // The share of a 100-group that counts towards the HOAI base (stored as
  // creditableCosts by the cost allocator); without it, in full.
  const creditableFactors = {};
  (Array.isArray(dinData) ? dinData : [dinData]).forEach((group) => {
    if (
      group?.number &&
      Number(group.costs) &&
      group.creditableCosts != null
    ) {
      creditableFactors[String(group.number).trim()] =
        Number(group.creditableCosts) / Number(group.costs);
    }
  });

  // A 100-group (level 0) carries the creditable amount, its sub-groups
  // (410 = level 1, 411 = level 2) are listed below it with their costs only.
  let tableContent = '';
  dinPositions.forEach((din) => {
    const number = String(din.number);
    const level = number.endsWith('00') ? 0 : number.endsWith('0') ? 1 : 2;
    const factor = creditableFactors[number] ?? 1;
    const rowVars = {
      '%%dinPositionNumber%%': din.number || '',
      '%%dinPositionLabel%%': din.label || '',
      '%%dinPositionDescription%%': din.description || '',
      '%%dinPositionCosts%%': numberToString(din.costs || 0, 2),
      '%%dinPositionCreditableCosts%%':
        level === 0
          ? numberToString(Math.round(100 * (din.costs || 0) * factor) / 100, 2)
          : '',
      '%%dinPositionClassName%%': `din-level-${level}`,
    };

    let row = rowTemplate;
    Object.entries(rowVars).forEach(([key, value]) => {
      row = row.replaceAll(key, value);
    });
    tableContent += row;
  });

  return (
    content.substring(0, startIdx) +
    tableContent +
    content.substring(endIdx + rowEndKey.length)
  );
}

/**
 * Generates HOAI calculation content for preview
 * Mirrors backend: CreatePdf.php:getHoaiCalculation()
 */
function generateHoaiContent(template, positions, hoaiDataArray) {
  if (!template?.content || !positions?.length) return '';

  // Collect all HOAI IDs from positions
  const hoaiIds = [
    ...new Set(
      positions
        .filter((p) => p.hoaiId || p.hoai_id)
        .map((p) => p.hoaiId || p.hoai_id),
    ),
  ];

  if (!hoaiIds.length) return '';

  let result = '';

  hoaiIds.forEach((hoaiId) => {
    const hoai = Array.isArray(hoaiDataArray)
      ? hoaiDataArray.find((h) => h.id === hoaiId)
      : hoaiDataArray?.id === hoaiId
        ? hoaiDataArray
        : null;
    if (!hoai) return;

    // Replace DIN positions
    let content = replaceDinPositions(
      template.content,
      hoai.din?.dinData || hoai.din?.din_data,
      hoai.din?.year,
    );

    // Replace HOAI variables
    const hoaiVars = buildHoaiVars(hoai);
    Object.entries(hoaiVars).forEach(([key, value]) => {
      content = content.replaceAll(key, value || '');
    });

    result += content;
  });

  return result;
}

function generateWorkContractValueKeyMap({ data }) {
  const map = {
    '%%employeeSalutation%%': {
      value: data?.employee?.salutation || '',
      fallback: '',
    },
    '%%employeeFirstName%%': {
      value: data?.employee?.firstName || '',
      fallback: '',
    },
    '%%employeeLastName%%': {
      value: data?.employee?.lastName || '',
      fallback: '',
    },
    '%%employeeId%%': {
      value: data?.employee?.id || '',
      fallback: '',
    },
    '%%employeeIsCeo%%': {
      value: data?.employee?.isCeo ? 'ceo' : '',
      fallback: '',
    },
    '%%employeePhone%%': {
      value: data?.employee?.phone || '',
      fallback: '',
    },
    '%%employeeEmail%%': {
      value: data?.employee?.email || '',
      fallback: '',
    },

    '%%country%%': {
      value: data?.employee?.country || '',
      fallback: '',
    },
    '%%zipCode%%': {
      value: data?.employee?.zipCode || '',
      fallback: '',
    },
    '%%city%%': {
      value: data?.employee?.city || '',
      fallback: '',
    },
    '%%street%%': {
      value: data?.employee?.street || '',
      fallback: '',
    },
    '%%houseNumber%%': {
      value: data?.employee?.houseNumber || '',
      fallback: '',
    },
    '%%addressAddition%%': {
      value: data?.employee?.addressAddition || '',
      fallback: '',
    },

    '%%contractPosition%%': {
      value: data?.position || '',
      fallback: '',
    },
    '%%contractPreset%%': {
      value: data?.preset?.body || '',
      fallback: '',
    },
  };
  return map;
}

export default ExoDocumentPreview;
